/**
 * Confetti — zero-dependency canvas confetti with quasi-steady flat-plate aerodynamics.
 *
 * Physics
 * -------
 * Every piece is a thin rigid rectangular plate with a real 3D orientation (quaternion).
 * Aerodynamic loads use the quasi-steady flat-plate model (Andersen, Pesavento & Wang, 2005),
 * which is what makes real paper alternate between fluttering (side-to-side gliding) and
 * tumbling (continuous end-over-end rotation):
 *
 *   F_normal      = -1/2 rho A C_N |v.n| (v.n) n     pressure on the face
 *   F_tangential  = -1/2 rho A C_T |v_t| v_t         skin friction along the face
 *   F_rotational  = +1/2 rho A C_R c (w x v)         Kutta-Joukowski / Magnus-like lift
 *   T_pitch       = +1/2 rho A C_M c (v.e1)(v.n)     centre-of-pressure shift -> flutter
 *
 * Mass is rho_paper * A * thickness, so plate AREA CANCELS OUT of every acceleration and all
 * that survives is one scalar per paper type, k = airCoefficient / thickness [1/px]. Terminal
 * velocity is then simply sqrt(gravity / (k * C_N)).
 *
 * The pile
 * --------
 * The drift is a 2D occupancy grid: one byte per cell, counting how many settled pieces
 * cover it. A landing piece asks the grid whether the cell it is in holds paper, so it
 * falls through a hollow and lands on whatever is at the bottom of it.
 *
 * Three earlier models tracked a single height per column instead, and every one of them
 * failed the same way. A column height cannot express a hollow — it assumes the paper under
 * it is a solid stack down to the floor. That holds while paper only falls and breaks the
 * moment a gust takes some out of the middle, after which the surface claims paper that is
 * not there and everything landing later builds on the lie. Measured on the last of them:
 * undisturbed it was clean, with no cave over 25px anywhere; after sweeping, caves up to
 * 91px and 17% of columns reporting a surface with nothing beneath it.
 *
 * Cost is one byte per cell: a 2560x1400 stage at 4px cells is 224KB, and marking a piece
 * touches about four cells. There is no readback from the canvas.
 *
 * On budgets
 * ----------
 * There are deliberately no caps here: none on live pieces, none on remembered ones, no
 * maximum pile depth, and no batched renderer. An earlier version had all of those, and they
 * interacted in ways that could not be reasoned about — paper vanishing mid-air, settling
 * onto invisible surfaces, lining up along a hidden ceiling, bleaching white. Measured at
 * ~1400 live pieces, a step cost 0.03ms against a 33ms frame, so none of it was paying for
 * itself. Add a budget back when a profile names something specific, and when you do, make
 * sure it cannot change what the simulation looks like.
 *
 * The two clamps that remain, maxSpin and maxSpeed, are numerical guards rather than budgets:
 * they stop the integrator producing NaN under extreme config and never bind in normal use.
 *
 * Usage
 * -----
 *   import Confetti from './Confetti';
 *
 *   const c = new Confetti();                         // the whole viewport
 *   const c = new Confetti({ container: el, pile: true, interactive: true });
 *   c.burst('fountain', { count: 400 });
 *   c.stream({ preset: 'rain', rate: 120, duration: 5000 });
 *   c.stream('celebrate');                           // a preset that carries its own numbers
 *   c.blow(x, y, { radius: 120, strength: 700 });     // sweep paper aside by hand
 *   c.destroy();
 *
 * Colours
 * -------
 * The default palette reads the brand custom properties the app publishes on :root, so one
 * call is right on both Gamefound and IdeaFound and follows the light/dark switch. Status
 * colours (Urgent, Warning, Danger) are deliberately absent: they hold the same values on
 * both platforms because an error must look like an error, and they are not brand.
 */

/* ------------------------------------------------------------------ *
 * Math helpers
 * ------------------------------------------------------------------ */

const TAU = Math.PI * 2;
const DEG = Math.PI / 180;

// Sine lookup table — turbulence needs one sin() per particle per substep.
const LUT_SIZE = 2048;
const LUT_MASK = LUT_SIZE - 1;
const LUT_SCALE = LUT_SIZE / TAU;
const SIN_LUT = new Float32Array(LUT_SIZE);

for (let i = 0; i < LUT_SIZE; i++) {
    SIN_LUT[i] = Math.sin(i * TAU / LUT_SIZE);
}

const fastSin = (a) => SIN_LUT[a * LUT_SCALE & LUT_MASK];

const clamp = (v, lo, hi) => v < lo ? lo : v > hi ? hi : v;

const rand = (lo, hi) => lo + Math.random() * (hi - lo);

/**
 * True only for object literals. DOM elements, canvases and class instances must stay
 * opaque: merging one field-by-field would quietly replace it with a lifeless copy.
 */
function isPlain(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }

    const proto = Object.getPrototypeOf(value);

    return proto === Object.prototype || proto === null;
}

/** Recursive merge of plain objects; arrays and primitives are replaced wholesale. */
function merge(base, override) {
    if (!isPlain(override)) {
        return override === undefined ? base : override;
    }

    const out = isPlain(base) ? Object.assign({}, base) : {};

    Object.keys(override).forEach((key) => {
        out[key] = isPlain(base && base[key]) ? merge(base[key], override[key]) : merge(undefined, override[key]);
    });

    return out;
}

/** Accepts 5, [2, 8] or { min, max } and normalises to a [min, max] pair. */
function range(value, fallback) {
    if (value === undefined || value === null) {
        return fallback;
    }

    if (Array.isArray(value)) {
        return [value[0], value.length > 1 ? value[1] : value[0]];
    }

    if (isPlain(value)) {
        return [value.min, value.max === undefined ? value.min : value.max];
    }

    return [value, value];
}

/* ------------------------------------------------------------------ *
 * Colour parsing — a throwaway 2D context resolves every CSS colour
 * form the browser understands, and var() is looked up against the
 * element the confetti renders into.
 * ------------------------------------------------------------------ */

let colorProbe = null;

const CSS_VAR = /^var\(\s*(--[^,)\s]+)\s*(?:,\s*([\s\S]+?)\s*)?\)$/;

/**
 * @param {string|number[]} input Any CSS colour, a var() reference, or an [r, g, b] triple.
 * @param {Element} [scope] Element the custom property is resolved against.
 * @param {number} [depth] Recursion guard for var() chains.
 */
function parseColor(input, scope, depth = 0) {
    if (Array.isArray(input)) {
        return [input[0] | 0, input[1] | 0, input[2] | 0];
    }

    const text = String(input).trim();
    const cssVar = depth < 4 ? CSS_VAR.exec(text) : null;

    if (cssVar) {
        const host = scope && scope.nodeType === 1 ? scope : document.documentElement;
        const value = getComputedStyle(host).getPropertyValue(cssVar[1])
            .trim();

        return parseColor(value || cssVar[2] || '#ffffff', scope, depth + 1);
    }

    const hex = /^#([0-9a-f]{3,8})$/i.exec(text);

    if (hex) {
        const d = hex[1];

        if (d.length === 3 || d.length === 4) {
            return [parseInt(d[0] + d[0], 16), parseInt(d[1] + d[1], 16), parseInt(d[2] + d[2], 16)];
        }

        return [parseInt(d.slice(0, 2), 16), parseInt(d.slice(2, 4), 16), parseInt(d.slice(4, 6), 16)];
    }

    const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(text);

    if (rgb) {
        return [+rgb[1] | 0, +rgb[2] | 0, +rgb[3] | 0];
    }

    if (!colorProbe && typeof document !== 'undefined') {
        const probe = document.createElement('canvas');

        probe.width = 1;
        probe.height = 1;
        colorProbe = probe.getContext('2d', { willReadFrequently: true });
    }

    if (colorProbe) {
        colorProbe.clearRect(0, 0, 1, 1);
        colorProbe.fillStyle = '#000';
        colorProbe.fillStyle = text;
        colorProbe.fillRect(0, 0, 1, 1);

        const px = colorProbe.getImageData(0, 0, 1, 1).data;

        return [px[0], px[1], px[2]];
    }

    return [255, 255, 255];
}

/* ------------------------------------------------------------------ *
 * Paper
 * ------------------------------------------------------------------ */

/**
 * Cuts of paper reused by every palette: streamers, near-squares, a ribbon and a thin foil
 * flake. Only the colours change between platforms; a palette may be shorter than this list.
 */
const PAPER_CUTS = [
    { albedo: 0.80, width: 9, height: 14, variation: 0.30, share: 1 },
    { albedo: 0.90, width: 8, height: 8, variation: 0.35, share: 0.85 },
    { albedo: 0.82, width: 7, height: 18, variation: 0.40, share: 0.8 },
    { albedo: 1.00, width: 6, height: 12, variation: 0.45, share: 0.55, thickness: 0.09 },
    { albedo: 0.88, width: 10, height: 10, variation: 0.30, share: 0.7 },
    { albedo: 0.84, width: 8, height: 15, variation: 0.35, share: 0.9 },
];

const cutPaper = (colors) => colors.map((color, index) =>
    Object.assign({ color }, PAPER_CUTS[index % PAPER_CUTS.length]));

/**
 * Brand colours only.
 *
 * The branding ColorPalette also carries Urgent, Warning, Danger, Success and Info, and it
 * is tempting to reach for them when a mix needs more hues — but those are STATUS colours,
 * not brand: they hold the same values on Gamefound and IdeaFound precisely because an
 * error has to look like an error on both. Celebration paper in the platform's error red
 * is a category mistake. Only the Brand/Primary and Accent families belong here, which is
 * also how Styles/_basics.root.scss groups them.
 *
 * The platform palette reads the custom properties the app publishes on :root, so one call
 * is right on both Gamefound and IdeaFound and follows the light/dark switch too. That is
 * what restricts it to the four config-driven brand tokens.
 *
 * The endgame palette below is the exception that proves the rule, and says why in its own
 * comment: it is pink because the product already paints that phase pink, and it uses
 * literal colours because paper is printed stock rather than a UI surface.
 */
const PALETTES = {
    platform: cutPaper([
        '#ffffff',
        'var(--config-color-accent, #1baa64)',
        'var(--config-color-accent-variant, #27e28b)',
    ]),

    /**
     * Endgame: the last ten minutes, when every pledge pushes the deadline back out again.
     *
     * Pink, because that is what the endgame already is in the product. The overtime card
     * renders its icon with _tc--urgent, projectLabelTypes.endgame maps to the "urgent" theme,
     * and the pledge button switches to it the moment overtime starts:
     *
     *   isCrowdfundingOvertimeActive ? 'urgent' : 'accent'
     *
     * Literal colours rather than the tokens, and deliberately so. --config-color-urgent is
     * hsl(346, 64%, 58%): a UI colour, desaturated to sit behind text without shouting. Paper
     * is not a UI surface — printed confetti is saturated stock, and the token rendered as
     * paper comes out dusty. These are the same hue at 346 degrees taken to the saturation a
     * print shop would actually cut, so the mix reads as the endgame without looking washed.
     *
     * Three papers is enough. Two pinks give the mix depth as pieces turn and catch the light
     * differently, and the white carries the sparkle — it is the brightest thing on screen
     * without being a colour that has to belong to anybody.
     *
     * White sits at albedo 0.9 rather than 1: shading runs ambient + albedo * diffuse * 0.55
     * to 0.92, which tops out around 0.88 here, so nothing clips to flat white and the pieces
     * keep their edges against the page.
     */
    endgame: [
        { color: '#f7456f', albedo: 0.94, width: 9, height: 13, variation: 0.35, share: 1 },
        { color: '#ff7092', albedo: 1.00, width: 7, height: 16, variation: 0.40, share: 0.8, thickness: 0.09 },
        { color: '#ffffff', albedo: 0.90, width: 8, height: 11, variation: 0.35, share: 0.6 },
    ],
};

/* ------------------------------------------------------------------ *
 * Defaults
 * ------------------------------------------------------------------ */

const DEFAULTS = {
    /**
     * The element to render into. Leave it out and the confetti goes over the viewport: a
     * fixed overlay on the window, floor at the bottom of the screen, staying put while the
     * page scrolls underneath.
     *
     * Anything else is just an element, including the page itself. Pass document.documentElement
     * (or document.body) and the stage is the whole document: paper falls its full length and
     * settles on the bottom of the PAGE, scrolling with the content. There is no separate mode
     * for that — the ordinary container path already sizes to the element, follows it as it
     * grows, and reads the pointer relative to it.
     *
     * A container must be positioned — anything but position: static — because the canvas
     * layer is absolutely positioned and sized against it. The plugin checks and throws
     * rather than setting the property itself: styles belong to whoever owns the layout, and
     * quietly making an element a containing block relocates every absolutely positioned
     * descendant that was resolving against the initial containing block. On <body> in a real
     * application that is most of the page.
     */
    container: null,
    zIndex: 9999,
    /** Device-pixel-ratio cap — 2 is plenty and halves fill cost on 3x phones. */
    maxDpr: 2,
    /** Thin things out and calm them down when the user asked for less motion. */
    respectReducedMotion: true,
    /**
     * Preset launch speeds are tuned for a ~900px-tall stage, or a ~1600px-wide one under
     * scaleAxis: 'width'. 'auto' rescales them, and the jet with them, to the real container so a
     * preset reads the same in a 380px hero band and full screen: with gravity fixed, v
     * proportional to sqrt(the dimension it crosses) keeps trajectory shapes similar. A number
     * forces a fixed multiplier; your own speed is never rescaled.
     */
    emitScale: 'auto',
    /** Freeze the simulation while off-screen or on a hidden tab. */
    autoPause: true,

    papers: PALETTES.platform,

    physics: {
        /** px/s^2. 1500 reads as "real gravity" at typical CSS pixel scales. */
        gravity: 1500,
        /**
         * k = airCoefficient / thickness. With aero.normal this fixes terminal velocity:
         * v_term = sqrt(gravity / (k * aero.normal)). Defaults give ~155 px/s.
         */
        airCoefficient: 0.004,
        /**
         * A cannon expels a slug of air together with the paper, and a piece inside that jet
         * sees far less drag than one drifting in still air. Without this, quadratic drag
         * stops a piece launched at 1300 px/s within ~35 px and no arc is possible — which is
         * also true of real confetti, and why real cannons rely on the air they carry.
         * Applied only to pieces launched well above their own terminal velocity, and they
         * cannot land while inside it, so a fountain may fire from below the floor line.
         */
        launchJet: { drag: 0.12, seconds: 0.4 },
        /** Steady ambient wind, px/s. */
        wind: { x: 0, y: 0 },
        /** Per-particle eddies: each piece has its own phase, so they never move in lockstep. */
        turbulence: { amplitude: 220, frequency: 0.85 },
        aero: {
            normal: 1.9,
            tangent: 0.09,
            rotational: 0.75,
            moment: 0.35,
            spinDampingLinear: 0.40,
            spinDampingQuadratic: 0.030,
        },
        /**
         * Numerical guards, not budgets: they keep the integrator from producing NaN under
         * extreme config and do not bind in normal use.
         */
        maxSpin: 45,
        maxSpeed: 4000,
        /** Fixed-timestep integration keeps behaviour identical at 30 and 144 Hz. */
        timeStep: 1 / 120,
        /** Simulated time per frame is capped at timeStep * maxSubsteps, so 1/15 s here. */
        maxSubsteps: 8,
        /** Camera focal length for the pseudo-3D size falloff. */
        perspective: 900,
        /**
         * How far a piece may drift in depth, as a fraction of `perspective`. Beyond it the
         * piece bounces back, the way it bounces off a side wall.
         *
         * Depth needs a wall because nothing else bounds it. There is no floor to land on in
         * z, no gravity to pull it back, and the rotational lift term keeps feeding vz for the
         * whole fall -- a tumbling piece at terminal velocity has a large relative vertical
         * speed, which is exactly what that term turns into sideways and depthwise force. So z
         * random-walks, while the projection focal / (focal + z) has a pole at z = -focal.
         *
         * That pole was reachable. Measured on a stage 12000px tall -- a body several screens
         * long -- with 600 pieces in free fall, the largest piece on screen grew like this:
         *
         *    4s   2.1x its own size
         *   10s   3.4x
         *   12s   7.6x
         *   20s  12.8x
         *   22s  224x, and pieces starting to cross behind the camera
         *
         * Past the pole the scale goes negative and the piece is skipped instead of drawn, so
         * the visible result was paper swelling to absurd size and then blinking out. It only
         * showed on long falls: on a viewport-height stage a piece is down in about six
         * seconds and rarely passes 2x.
         *
         * 0.4 caps the scale at 0.71x-1.67x, and the spawn range of `depth` sits inside it
         * with room to spare. The bounce is what keeps the depth alive: clamping z and zeroing
         * vz instead would pin a piece to the wall, because the force that drove it there is
         * still pushing. Measured with the wall in, over two minutes of falling: scale never
         * passes the 1.67x ceiling and nothing reaches the camera. |vz| does creep up, since an
         * elastic bounce keeps what the lift term adds -- peak went 255 to about 320 px/s -- but
         * it plateaus there and the mean stays around 85, so it rattles rather than winds up.
         *
         * 0 disables the wall.
         */
        depthLimit: 0.4,
    },

    render: {
        /** Direction the light travels, in view space (+z points away from the viewer). */
        light: { x: -0.35, y: -0.78, z: -0.52 },
        ambient: 0.60,
        diffuse: 0.72,
        specular: 1.0,
        /** Reverse of the sheet is darker unless the paper defines an explicit `back`. */
        backsideFactor: 0.8,
        /** How much distant pieces wash out. */
        depthFog: 0.2,
    },

    life: {
        /** Seconds before a piece fades out; 0 = derive from container height. */
        seconds: 0,
        /** Length of the fade-out tail, seconds. */
        fade: 0.7,
        /** Safety net for pieces that never land, e.g. ones held up by wind. */
        max: 30,
    },

    /**
     * Do the container sides hold paper in?
     *
     * false (default) leaves them open: a piece that drifts past the edge carries on down
     * and out, the way paper leaves a table. true turns them into real walls that reflect
     * pieces in flight, for a closed box; a number sets how bouncy (0 = dead stop, 1 =
     * perfectly elastic).
     *
     * Reflecting pieces is the only honest way to keep paper in. Letting it settle past the
     * edge instead banks it against a boundary the container does not have: everything that
     * overhangs folds onto the first and last column of the drift, and the edges grow to
     * twice the depth of the middle.
     */
    walls: false,

    /** false, true, or an object — see PILE_DEFAULTS. */
    pile: false,

    /**
     * How long the drift stays after the last emission, then a `fade`-second fade-out of the
     * pile canvas. 0 keeps it for the life of the instance.
     */
    pileLife: { seconds: 0, fade: 1.5 },

    /**
     * Let the pointer sweep paper around: false, true, or an object — see INTERACT_DEFAULTS.
     * The canvas layer stays pointer-events: none either way, so the page underneath keeps
     * every click; moves are read from the container (or the window, in viewport mode).
     */
    interactive: false,

    /** onSettle(x, y) whenever a piece comes to rest on the pile. */
    onSettle: null,

    /**
     * onIdle() when the simulation parks: the last piece has landed or left, and no stream
     * is still feeding it. This is the moment an owner can destroy() the instance without
     * cutting anything short.
     *
     * Parking is not the same as pausing. autoPause freezes the step while the stage is
     * off-screen or the tab is hidden, but the loop stays armed and paper is still in the
     * air, so onIdle does not fire for it.
     *
     * A settled pile does not keep the simulation busy — it is baked into its own canvas and
     * needs no frames — so with pile: true this fires while the paper is still lying there.
     * Destroying then takes the pile off the screen with it; that is the caller's call.
     *
     * Fires once per busy-to-idle transition, so an interactive instance that is woken by a
     * gust and settles again reports each time.
     */
    onIdle: null,
};

/** Stands in for a frame id while the loop is inside a frame, so _wake() knows it is live. */
const RUNNING = -1;

/* Where the last shaped gust points. Module scratch, so shaping a push allocates nothing. */
let gustX = 0;
let gustY = 0;

/**
 * Points one push from the pointer.
 *
 * A plain radial field has a dead spot exactly under the nozzle: a piece directly below the
 * pointer sits where the sideways component is zero and the upward component is cancelled by
 * the lift offset, so it receives nothing at all -- measured across the field, 0.00 of the
 * gust strength straight down against 0.94 straight sideways. That spot is precisely where
 * the drift is when the pointer is held over it.
 *
 * Air near a floor has nowhere to go but sideways: it spreads out under the source and turns
 * upwards above it. Sideways share is |dx|/d, which spreads smoothly, with a floor under it
 * so a piece almost directly above is still pushed aside rather than left hovering.
 *
 * `lift` is the share acting upwards level with the pointer; higher up, the mix carries it to
 * the whole push. Never downwards, and never nothing.
 */
function shapeGust(dx, dy, distance, radius, lift) {
    // Screen y grows downwards, so a piece above the pointer has a negative dy.
    const up = clamp(-dy / radius, 0, 1);
    const across = distance > 1 ? Math.abs(dx) / distance : 1;

    gustX = (dx < 0 ? -1 : 1) * (across < 0.35 ? 0.35 : across) * (1 - up * 0.5);
    gustY = -(lift + (1 - lift) * up);
}

const INTERACT_DEFAULTS = {
    /** Reach of the gust around the pointer, px. */
    radius: 200,
    /** Share of the gust that acts upwards. */
    lift: 0.5,
    /**
     * Air the pointer moves while it is simply sitting there, px/s. Moving it adds to this
     * rather than replacing it.
     *
     * Strength drawn from pointer travel alone means the pointer stops existing the moment
     * the hand does, which is not how anything that blows air behaves -- and it left paper
     * that had lost its footing dropping straight past the pointer to the floor, because by
     * then there was nothing there to catch it. Set to 0 for the old motion-only behaviour.
     */
    idle: 260,
    /**
     * How many times a second the gust reaches into the drift and picks paper up. The air
     * still pushes paper that is already flying on every single frame; this is only about
     * taking new pieces out of the pile.
     *
     * That is where the cost is. Every lift changes the drift, so the grid columns under the
     * gust are rebuilt and that patch of the pile canvas is repainted -- 0.68ms of a 1.2ms
     * frame went on the repaint alone, every frame, for as long as the pointer sat there.
     *
     * Sampling it less often does not slow the clearing down, because the gust takes whatever
     * is in its circle and the circle refills in between: measured at a quarter of the rate,
     * pieces lifted per frame went 16.2 to 14.6. The exposure that has built up since the last
     * lift is handed over with it, so paper carried out of the scene per second is unchanged.
     */
    liftRate: 30,

    /**
     * Strength of the one gust a tap gets, as a multiple of `idle`.
     *
     * A pointer that hovers accumulates: `idle` is what it moves in a single frame, and a
     * hand resting for a moment delivers dozens of those. A tap has no dwell time at all, so
     * one frame worth of it would be invisible. 15 is about what a quarter second of hovering
     * delivers at 60Hz, which is roughly how long a tap feels like it lasts.
     *
     * A multiple rather than an absolute number so that tuning `idle` carries the tap with
     * it. 0 turns taps off, and so does an `idle` of 0, there being no steady gust to take a
     * multiple of.
     */
    tap: 15,
};

const PILE_DEFAULTS = {
    /** 'bottom', a fraction of the height (0..1), or an absolute px offset from the top. */
    floor: 'bottom',
    /**
     * Size of one occupancy cell, px. The drift is tracked as a 2D grid of cells that are
     * either holding paper or not — not as a height per column.
     *
     * A column height cannot express a hollow: it assumes the paper under it is a solid
     * stack down to the floor. That is true while paper only falls, and false the moment a
     * gust takes some out of the middle, after which the surface claims paper that is not
     * there and everything landing later builds on the lie. Measured on the column model:
     * undisturbed, no cave over 25px anywhere; after sweeping, caves up to 91px and 17% of
     * columns reporting a surface with nothing under it.
     *
     * Memory is one byte per cell: a 2560x1400 stage at 4px costs 224KB.
     */
    binSize: 4,
    restitution: 0.18,
    friction: 0.55,
    bounces: 1,
    /**
     * How slowly a piece must be travelling, in px/s, before the drift accepts it. Above
     * this it skids across the surface instead of stopping where it touched.
     *
     * Without it every piece freezes on first contact whatever its speed: measured, they
     * came to rest at a median of 96 px/s while the drift built and 135 px/s as they fell
     * back after a sweep, against a terminal velocity of 154. Half the paper was stopping
     * dead at nearly full speed, which is what makes the heap look like it is in a hurry to
     * swallow everything, and leaves the pieces lying where they hit rather than where they
     * would have come to rest.
     */
    settleSpeed: 35,
    /**
     * Chance that a piece the gust lifts is carried out of the scene rather than put back
     * down somewhere. Without it paper is conserved exactly — measured, 2319 pieces settled
     * before a sweep and 2319 after — so the drift can only ever be pushed around, never
     * cleared, which is not what blowing at it implies.
     *
     * Per lift, and the gust lifts at interactive.liftRate, so this is per lift per piece and
     * independent of the frame rate and of the mouse. Measured over four passes across a
     * 3150-piece drift, 0.068 carries off about 800 of them.
     *
     * Carried off, not deleted where it lies: the piece is thrown clear and fades as it
     * goes, because paper blinking out mid-drift is exactly what a settled pile should
     * never do. It leaves the settled records immediately either way, which is where the
     * cost is — a repaint runs about 2.5ms at 5800 pieces.
     */
    sweepAway: 0.068,
    /**
     * How fast a skidding piece loses its speed, per second: e^-slide of it remains.
     *
     * Kept deliberately short. The skid exists only so paper is not frozen mid-flight at full
     * speed; it is not meant to be seen. Let it run and the pieces glide:
     *
     *   slide   median travel   p90 travel   p90 duration
     *      30           2.8px       21.6px          233ms   (visible as a glide)
     *      90           0.7px        6.1px           50ms
     *     200           0.3px        3.7px           17ms
     *
     * Measured over a drift of 12 drops swept four times, 5000 or so skids per setting. At 90
     * the p90 skid is shorter than a piece is wide and over inside three frames.
     *
     * Set settleSpeed high enough that nothing ever skids if the phase is unwanted at all.
     */
    slide: 90,
    /** Vertical squash of a lying piece, faking a floor seen at a shallow angle. */
    foreshorten: 0.5,
    /**
     * How far a landing piece works its way into the paper under it, as a fraction of its
     * own thickness. 0 rests each piece exactly on top of the last, which is how rigid boxes
     * stack, not paper: quads that merely touch leave gaps the size of a piece between them,
     * and against a dark stage those read as holes. Paper is floppy and nestles.
     *
     * Trading gaps against depth, measured over 2400 settled pieces:
     *
     *   nestle   gaps    median gap   drift depth
     *   0.45     15.7%   2px          113px
     *   0.55      9.7%   2px           92px
     *   0.65      4.8%   2px           70px
     *   0.70      2.6%   1px           63px
     *   0.78      0.6%   1px           43px
     *
     * Over 1 is not meaningful, but there is no physical ceiling below it: this is a 2D view
     * of a heap whose pieces also lie behind one another, so overlapping several deep is what
     * the projection of a real drift looks like.
     */
    nestle: 0.7,
    /**
     * Angle of repose, as px of drop per px of travel. A piece landing on a slope slides
     * down it until the slope is this shallow. This is the only rule that shapes a heap,
     * and it reads the same surface the paper itself wrote — so unlike the smoothing pass
     * it replaces, it cannot invent height that no piece put there. Without it a fountain
     * stacks a needle straight up out of the container. 0 lets paper pile vertically.
     */
    repose: 0.35,
};

/* ------------------------------------------------------------------ *
 * Emitter presets
 * ------------------------------------------------------------------ */

const EMIT_DEFAULTS = {
    count: 200,
    /** Spawn centre in normalised container coordinates (0..1). */
    origin: { x: 0.5, y: 0.5 },
    /** Spawn box size, also normalised; 0 means a point source. */
    box: { x: 0, y: 0 },
    /** Degrees; 0 = right, 90 = straight up. */
    angle: 90,
    /** Total cone width in degrees. 360 = omnidirectional. */
    spreadAngle: 60,
    speed: [400, 900],
    /** Initial |angular velocity|, rad/s. */
    spin: [3, 26],
    /** Pseudo-3D depth and its rate of change. */
    depth: [-140, 160],
    depthSpeed: [-60, 60],
    /**
     * Seconds of launch jet for this emission; null takes physics.launchJet.seconds. Rescaled
     * to the stage like the launch speed.
     *
     * It sits here rather than only in physics because how far a cannon throws is a property
     * of that cannon, not of the stage it fires into -- and the jet is most of what moves
     * reach at all. Measured on one cannon at 62 degrees, 3000 pieces, leading edge of the
     * spray one second in, mean of five runs:
     *
     *   variant                        reach     vs stock
     *   stock (0.4s, stock speed)      470px        1.00x
     *   celebrate-cannons' pair        845px        1.80x
     *     its 2.4s jet alone           634px        1.35x
     *     its raised speed alone       558px        1.19x
     *
     * Speed alone buys very little: drag goes as v squared and the aerodynamic torque turns a
     * fast piece broadside sooner, so on the stock jet a doubled launch speed measured within
     * noise of doing nothing and a tripled one measured slightly worse. The jet is also flat
     * past about 2.4s -- 2.8s and 3.2s both measured inside the run-to-run noise of it -- so
     * roughly 1.8x is the practical ceiling of these two levers together.
     *
     * The cost of a long jet is that a piece cannot settle while it is inside one, so the
     * drift starts forming that many seconds late.
     */
    jetSeconds: null,

    /** Which stage dimension this emission crosses; its speed and jet rescale against that side. */
    scaleAxis: 'height',

    /**
     * Scale the amount of paper with the stage: false, true for `scaleAxis`, or an axis of its
     * own (rain falls down the height but spreads across the width). Streams scale their rate by
     * it too. Linear, unlike speed: area to cover, not trajectory shape.
     */
    scaleCount: false,

    /** Restrict this emission to a subset of `papers`, by index or by colour. */
    papers: null,
};

/**
 * Instance options a preset is allowed to carry, applied through configure() when it names
 * one. Deliberately a short list rather than "anything in DEFAULTS": these are the two that
 * say what an effect IS rather than where it lives, and both already support being switched
 * after construction. Nothing here can move the instance to another container.
 */
const EFFECT_OPTIONS = ['pile', 'interactive', 'pileLife'];

/** Everything in a rich preset that is not an emit override. */
const EFFECT_KEYS = ['rate', 'duration', 'emit'].concat(EFFECT_OPTIONS);

/** What a stream runs at when neither the caller nor the preset says. */
const STREAM_DEFAULTS = {
    /** Pieces per second. */
    rate: 80,
    /** Milliseconds. 0 runs until stop(). */
    duration: 0,
};

/**
 * A preset is either an emission -- a spec object, the name of another preset, or an array
 * of those -- or the richer { rate, duration, ...effectOptions, ...emitOverrides, emit },
 * which carries the whole effect so that a screen asking for it states no numbers of its
 * own. The `emit` key is what tells the two apart: no emission option is called that.
 *
 * The rich form exists because rate and duration are as much a part of "this is what our
 * celebration looks like" as the launch angle is, and they were the two things every call
 * site had to repeat. rate and duration are stream-only; burst() ignores them and takes
 * everything else.
 *
 * Anything left over is an emit override, applied to every emitter the `emit` resolves to.
 * merge is deep, so { origin: { y: 0.5 } } over a two-sided emission moves both cannons up
 * while each keeps its own x. These travel separately from a caller's overrides, because
 * only a CALLER naming a speed means "this exact speed" -- a preset naming one still wants it
 * rescaled to the stage like any other preset speed.
 */
const PRESETS = {
    burst: { origin: { x: 0.5, y: 0.5 }, angle: 90, spreadAngle: 360, speed: [260, 900], count: 180 },
    fountain: { origin: { x: 0.5, y: 1.02 }, angle: 90, spreadAngle: 46, speed: [1100, 1750], count: 260 },
    'cannon-left': { origin: { x: -0.02, y: 0.92 }, angle: 62, spreadAngle: 50, speed: [1000, 1700], count: 220 },
    'cannon-right': { origin: { x: 1.02, y: 0.92 }, angle: 118, spreadAngle: 50, speed: [1000, 1700], count: 220 },
    sides: ['cannon-left', 'cannon-right'],
    /**
     * A pair crossing over the middle and arriving at the top edge, not past it.
     *
     * Angle buys reach, power does not: measured, one cannon reaches 69% of the width at 38
     * degrees and 55% at 62, and the jet a steep shot answers goes straight up instead.
     */
    'crossfire-left': {
        origin: { x: -0.02, y: 0.35 }, angle: 38, spreadAngle: 30,
        speed: [3700, 5500], count: 400, scaleAxis: 'width', scaleCount: true,
    },
    'crossfire-right': {
        origin: { x: 1.02, y: 0.35 }, angle: 142, spreadAngle: 30,
        speed: [3700, 5500], count: 400, scaleAxis: 'width', scaleCount: true,
    },
    crossfire: ['crossfire-left', 'crossfire-right'],
    rain: {
        origin: { x: 0.5, y: -0.06 }, box: { x: 1.1, y: 0.04 },
        angle: -90, spreadAngle: 34, speed: [30, 170], count: 160,
    },
    top: {
        origin: { x: 0.5, y: -0.04 }, box: { x: 1, y: 0 },
        angle: -90, spreadAngle: 60, speed: [180, 520], count: 200,
    },
    /**
     * The celebration, for screens that have something to celebrate. Twice the default rate
     * over half the run reads as a shower rather than a drizzle and lands the same amount of
     * paper, and it collects where it falls instead of vanishing at the floor line.
     *
     * Interactive, so the paper it leaves on the floor can be blown around afterwards. That
     * costs nothing while the pointer is still: the gust only runs on a move.
     */
    celebrate: {
        rate: 160,
        duration: 7500,
        pile: true,
        interactive: true,
        emit: 'rain',
    },
    /**
     * The same celebration fired from two cannons instead of falling out of the sky. Same
     * pile, same pointer; the emission is where they part.
     *
     * A real cannon goes off once, so this is a burst and carries no rate or duration: both
     * sides throw everything they have in a single frame and the paper takes it from there.
     * count is per emitter, so this is 8160 pieces in one go.
     *
     * Fired from halfway up rather than from 0.92, which put them almost on the floor. The
     * height is normalised, so it is the same proportion of a phone as of a desktop and
     * cannot land off-screen on a short viewport the way a fixed pixel offset can. Only y is
     * overridden; each cannon keeps the x that puts it just outside its own edge.
     *
     * The long jet and the raised speed together throw the spray 1.8x as far as the stock
     * cannon -- 845px against 470px one second in, measured through this preset. See
     * jetSeconds for why the jet does most of that and the speed alone would not have.
     */
    'celebrate-cannons': {
        pile: true,
        interactive: true,
        // Stated because the stage is shared: otherwise it inherits the last effect's expiry.
        pileLife: { seconds: 0 },
        origin: { y: 0.5 },
        count: 4080,
        speed: [2300, 4500],
        jetSeconds: 1.0,
        emit: 'sides',
    },
    /**
     * The crossfire as an effect. Spends on speed, not count: a page can carry several marks and
     * it is the counts that add up.
     */
    'celebrate-crossfire': {
        pile: true,
        interactive: true,
        pileLife: { seconds: 20, fade: 2 },
        jetSeconds: 2.2,
        emit: 'crossfire',
    },
    /** Rain until something stops it. Does not pile: over an open run the drift never stops growing. */
    'celebrate-forever': {
        rate: 200,
        duration: 0,
        pile: false,
        interactive: true,
        scaleCount: 'width',
        emit: 'rain',
    },
    /** A shower to follow a burst in with, done before the burst's own paper has landed. */
    'celebrate-shower': {
        rate: 120,
        duration: 4000,
        pile: true,
        interactive: true,
        pileLife: { seconds: 20, fade: 2 },
        scaleCount: 'width',
        emit: 'rain',
    },
};

/** First value that is not undefined, so caller beats preset beats default, in that order. */
function firstDefined(...values) {
    return values.find((value) => value !== undefined);
}

/** Everything in `source` except `keys`, as a new object. */
function omit(source, keys) {
    return Object.keys(source).reduce((out, key) => {
        if (!keys.includes(key)) {
            out[key] = source[key];
        }

        return out;
    }, {});
}

/** Those of `keys` that `source` actually sets, as a new object. */
function pickDefined(source, keys) {
    return keys.reduce((out, key) => {
        if (source[key] !== undefined) {
            out[key] = source[key];
        }

        return out;
    }, {});
}

/**
 * Splits a preset into its stream-level part and its emission, following names and nested
 * `emit` until it reaches one. An outer preset wins over the one it points at.
 *
 * `fromPreset` travels with the emission because this unwraps names that resolveSpec would
 * otherwise have resolved itself, and that is where it learns whether a speed was a caller's
 * choice or a preset's. Lose it and a preset's speeds stop being rescaled to the stage.
 */
function unwrapEffect(spec, fromPreset = false) {
    if (typeof spec === 'string') {
        const preset = PRESETS[spec];

        if (!preset) {
            throw new Error(`Confetti: unknown preset "${ spec }"`);
        }

        return unwrapEffect(preset, true);
    }

    if (isPlain(spec) && 'emit' in spec) {
        const inner = unwrapEffect(spec.emit, fromPreset);

        return {
            rate: firstDefined(spec.rate, inner.rate),
            duration: firstDefined(spec.duration, inner.duration),
            options: Object.assign({}, inner.options, pickDefined(spec, EFFECT_OPTIONS)),
            overrides: merge(inner.overrides, omit(spec, EFFECT_KEYS)),
            emit: inner.emit,
            fromPreset: inner.fromPreset,
        };
    }

    return { emit: spec, options: {}, overrides: {}, fromPreset };
}

/** Resolves a preset name / spec / array of either into a flat list of emit specs. */
/**
 * @param {*} spec      The emission: a spec object, a preset name, or an array of either.
 * @param {Object} [overrides]       What the CALLER passed. A speed here is taken literally.
 * @param {boolean} [fromPreset]     Whether `spec` came out of PRESETS.
 * @param {Object} [presetOverrides] What a rich preset carried. Applied under `overrides`,
 *                                   and a speed here is still a preset speed.
 */
function resolveSpec(spec, overrides, fromPreset, presetOverrides) {
    if (Array.isArray(spec)) {
        return spec.reduce(
            (acc, item) => acc.concat(resolveSpec(item, overrides, fromPreset, presetOverrides)), []);
    }

    if (typeof spec === 'string') {
        const preset = PRESETS[spec];

        if (!preset) {
            throw new Error(`Confetti: unknown preset "${ spec }"`);
        }

        return resolveSpec(preset, overrides, true, presetOverrides);
    }

    // A rich preset reaching here came from burst(), or from inside an array: take its
    // emission and let the stream-level keys fall away, which is what a one-shot means by them.
    if (isPlain(spec) && 'emit' in spec) {
        return resolveSpec(spec.emit, overrides, fromPreset, presetOverrides);
    }

    const base = merge(EMIT_DEFAULTS, spec || {});
    const fromEffect = presetOverrides ? merge(base, presetOverrides) : base;
    const resolved = overrides ? merge(fromEffect, overrides) : fromEffect;
    const chosenByCaller = !fromPreset && spec && spec.speed !== undefined
        || overrides && overrides.speed !== undefined;

    resolved.scaleSpeed = !chosenByCaller;

    return [resolved];
}

/**
 * Shade steps per side, per paper, that get their rgb() string remembered.
 *
 * Shade is continuous, and a fresh `rgb(r,g,b)` string per piece per frame cost two things:
 * the allocation, and -- far worse -- a CSS colour parse inside the canvas every time the
 * string is assigned to fillStyle. With a few thousand pieces in the air that is a few
 * hundred thousand parses a second, which is why a cannon burst hurt while the same paper
 * falling gently afterwards did not: the cost tracks how many pieces are drawn at once.
 *
 * SHADE_MAX is the ceiling of what the lighting can produce: ambient + albedo * diffuse +
 * specular, with headroom. 640 steps over it puts one step at 1/256th of shade 1.0, so the
 * quantisation is at most one 8-bit level on the brightest paper -- not a visible edge.
 * Filled lazily, so only the shades actually used are ever built.
 */
const SHADE_STEPS = 640;
const SHADE_MAX = 2.5;

/** Floats per remembered piece: x, aboveFloor, angle, halfWidth, halfHeight, paper, side, facing. */
const RECORD = 8;

/* ------------------------------------------------------------------ *
 * Confetti
 * ------------------------------------------------------------------ */

export default class Confetti {

    constructor(options = {}) {
        this.options = merge(DEFAULTS, options);
        this.destroyed = false;

        this._reduced = this.options.respectReducedMotion
            && typeof matchMedia === 'function'
            && matchMedia('(prefers-reduced-motion: reduce)').matches;

        this._count = 0;
        this._capacity = 0;
        this._time = 0;
        this._accumulator = 0;
        this._rect = null;
        this._rectStale = true;
        // Whether the paper canvas currently has anything on it. See _render.
        this._painted = false;
        this._pointerOn = false;
        this._pointerX = 0;
        this._pointerY = 0;
        this._liftDue = 0;
        this._lastFrame = 0;
        this._raf = 0;
        this._streams = [];
        this._pileTimer = 0;
        this._pileFadeTimer = 0;
        this._crestWatchers = [];
        this._paused = false;
        this._onScreen = true;
        this._pageVisible = true;
        this._fps = 0;
        this._worstFrame = 0;
        this._worstAt = 0;
        this._frameStamp = -1;
        this._touched = null;
        this._touchedCount = 0;
        this._fills = 0;
        this._width = 0;
        this._height = 0;
        this._dpr = 1;
        this._settledCount = 0;

        // Mount first: paper colours may be var() references that resolve against the layer.
        this._mount();
        this._buildPapers();
        this._buildLighting();
        this._allocate(256);
        this._resize();
        this._observe();
    }

    /* -------------------------------------------------------------- *
     * Setup
     * -------------------------------------------------------------- */

    /** Flattens paper definitions into the numbers the hot loop needs. */
    _buildPapers() {
        const source = this.options.papers && this.options.papers.length ? this.options.papers : PALETTES.platform;
        const air = this.options.physics.airCoefficient;
        const scope = this._layer || document.documentElement;

        const previous = this._papers ? this._papers.length : 0;

        this._papers = source.map((raw) => {
            const paper = merge({
                color: '#ffffff',
                back: null,
                albedo: 0.85,
                width: 9,
                height: 14,
                variation: 0.3,
                share: 1,
                thickness: 0.12,
                drag: 1,
            }, raw);

            const front = parseColor(paper.color, scope);
            const back = paper.back ? parseColor(paper.back, scope) : [
                front[0] * this.options.render.backsideFactor,
                front[1] * this.options.render.backsideFactor,
                front[2] * this.options.render.backsideFactor,
            ];

            return {
                front,
                back,
                albedo: paper.albedo,
                width: Math.max(0.5, paper.width),
                height: Math.max(0.5, paper.height),
                variation: clamp(paper.variation, 0, 0.95),
                share: Math.max(0, paper.share),
                // The single scalar the whole aerodynamic model reduces to.
                k: air / Math.max(0.01, paper.thickness) * paper.drag,
                color: paper.color,
                // Front and back shades, lazily filled -- see SHADE_STEPS. It hangs off the
                // paper so that rebuilding the palette throws the old strings away with it.
                shades: new Array(SHADE_STEPS * 2),
            };
        });

        // Cumulative shares, so picking a paper type is one linear scan.
        this._shareTotal = 0;
        this._shareCum = this._papers.map((paper) => {
            this._shareTotal += paper.share;

            return this._shareTotal;
        });

        if (this._shareTotal <= 0) {
            this._shareTotal = this._papers.length;
            this._shareCum = this._papers.map((_, i) => i + 1);
        }

        this._restamp(previous);
    }

    /** Normalises the light direction once. Shading itself is computed per piece, per frame. */
    /**
     * Points every piece at a paper that exists.
     *
     * Switching to a shorter palette leaves pieces in flight, and records in the drift, still
     * carrying an index into the palette they were born with. _render read that index with no
     * fallback and threw on every frame -- 247 of them in one sitting -- and a throw inside
     * the frame used to latch the loop dead, so a single palette change could stop the whole
     * plugin, buttons included.
     *
     * Wrapped rather than clamped to zero, so the pieces keep their spread across the new mix
     * instead of every one of them turning into the first colour.
     */
    _restamp(previous) {
        const count = this._papers.length;

        if (!count || previous <= count) {
            return;
        }

        if (this._paper) {
            for (let i = 0; i < this._count; i++) {
                if (this._paper[i] >= count) {
                    this._paper[i] %= count;
                }
            }
        }

        if (!this._settled || !this._settledCount) {
            return;
        }

        for (let k = 0; k < this._settledCount; k++) {
            const o = k * RECORD;

            if (this._settled[o + 5] >= count) {
                this._settled[o + 5] %= count;
            }
        }

        // The drift is baked, so its old colours stay on the canvas until it is repainted.
        this._markDirty(0, 0, this._width, this._height);
        this._wake();
    }

    _buildLighting() {
        const l = this.options.render.light;
        const len = Math.sqrt(l.x * l.x + l.y * l.y + l.z * l.z) || 1;

        this._lx = l.x / len;
        this._ly = l.y / len;
        this._lz = l.z / len;
    }

    /** `rgb()` for one face of one paper at a given shade. */
    _shadeColor(paper, side, shade) {
        const step = shade <= 0
            ? 0
            : shade >= SHADE_MAX ? SHADE_STEPS - 1 : shade * (SHADE_STEPS / SHADE_MAX) | 0;
        const slot = side * SHADE_STEPS + step;
        const known = paper.shades[slot];

        if (known !== undefined) {
            return known;
        }

        const base = side === 0 ? paper.front : paper.back;
        // The middle of the bucket, so the error is half a step either way rather than a
        // whole one downwards.
        const level = (step + 0.5) * (SHADE_MAX / SHADE_STEPS);
        const rgb = `rgb(${ clamp(base[0] * level, 0, 255) | 0 },`
            + `${ clamp(base[1] * level, 0, 255) | 0 },`
            + `${ clamp(base[2] * level, 0, 255) | 0 })`;

        paper.shades[slot] = rgb;

        return rgb;
    }

    _mount() {
        const target = this.options.container;

        this._viewport = !target || target === 'viewport';
        // What this instance was mounted on, kept for the life of it.
        this._mountedOn = target;
        this._container = this._viewport
            ? null
            : typeof target === 'string' ? document.querySelector(target) : target;

        if (!this._viewport && !this._container) {
            throw new Error('Confetti: container not found');
        }

        const layer = document.createElement('div');

        layer.className = 'gfu-confetti-layer';
        layer.setAttribute('aria-hidden', 'true');
        Object.assign(layer.style, {
            position: this._viewport ? 'fixed' : 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: String(this.options.zIndex),
        });

        this._layer = layer;

        if (this._pileOptions()) {
            this._pileCanvas = this._makeCanvas();
            this._pileCtx = this._pileCanvas.getContext('2d');
            layer.appendChild(this._pileCanvas);
        }

        this._canvas = this._makeCanvas();
        this._ctx = this._canvas.getContext('2d', { alpha: true });
        layer.appendChild(this._canvas);

        if (this._viewport) {
            document.body.appendChild(layer);
        }
        else {
            if (getComputedStyle(this._container).position === 'static') {
                throw new Error('Confetti: the container is position: static, so the canvas layer '
                    + 'has nothing to size itself against. Position it in the stylesheet that owns '
                    + 'the element, or leave the container out to render over the viewport.');
            }

            this._container.appendChild(layer);
        }
    }

    _makeCanvas() {
        const canvas = document.createElement('canvas');

        Object.assign(canvas.style, {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            display: 'block',
        });

        return canvas;
    }

    _pileOptions() {
        const pile = this.options.pile;

        if (!pile) {
            return null;
        }

        if (!this._pileCache) {
            this._pileCache = merge(PILE_DEFAULTS, pile === true ? {} : pile);
        }

        return this._pileCache;
    }

    /** The pile canvas is created on demand, so pile can be switched on at runtime. */
    _ensurePileCanvas() {
        if (this._pileCtx || !this._pileOptions()) {
            return;
        }

        this._pileCanvas = this._makeCanvas();
        this._pileCtx = this._pileCanvas.getContext('2d');
        this._pileCanvas.width = Math.round(this._width * this._dpr);
        this._pileCanvas.height = Math.round(this._height * this._dpr);
        this._pileCtx.setTransform(this._dpr, 0, 0, this._dpr, 0, 0);
        this._layer.insertBefore(this._pileCanvas, this._canvas);
    }

    /**
     * Where the container sits on screen, read only when something could have moved it.
     *
     * getBoundingClientRect has to return a layout that is up to date, so calling it from a
     * pointermove handler forces a synchronous reflow of the page whenever anything changed
     * since the last call -- and on a page running an animation, something always has.
     * Measured: 2.6us per pointer event with the layout left alone, 105us with a single DOM
     * change between events. A mouse reporting at 1kHz then spends 105ms of every second of
     * movement inside layout, which is felt as "the faster I move, the worse it gets" and has
     * nothing to do with how much paper is on screen.
     *
     * Resizing and scrolling are the things that move a container, and both already announce
     * themselves, so the read happens once after each of those and never during plain motion.
     */
    _containerRect() {
        if (this._viewport) {
            return null;
        }

        if (this._rectStale || !this._rect) {
            this._rect = this._container.getBoundingClientRect();
            this._rectStale = false;
        }

        return this._rect;
    }

    _interactOptions() {
        const config = this.options.interactive;

        if (!config) {
            return null;
        }

        if (!this._interactCache) {
            this._interactCache = merge(INTERACT_DEFAULTS, config === true ? {} : config);
        }

        return this._interactCache;
    }

    /**
     * Pointer tracking. Listens on the container rather than the canvas, so the layer can
     * stay click-through. The handler does no blowing of its own: it only records where the
     * pointer is, and the frame loop blows from there for as long as it stays.
     *
     * Blowing from the handler as well is both redundant and wrong. It fires once per pointer
     * report, so the same movement blew twice as hard on a mouse that reports twice as often,
     * and it stacked a second gust on top of the steady one whenever the hand moved -- which
     * is a broom, not a blower, and a blower is what this is.
     */
    _bindPointer() {
        const config = this._interactOptions();

        if (!config || this._pointerHost) {
            return;
        }

        const host = this._viewport ? window : this._container;

        this._onPointerMove = (event) => {
            const rect = this._containerRect();
            const x = event.clientX - (rect ? rect.left : 0);
            const y = event.clientY - (rect ? rect.top : 0);

            this._pointerX = x;
            this._pointerY = y;
            this._pointerOn = true;
            this._wake();
        };

        this._onPointerLeave = () => {
            this._pointerOn = false;
        };

        this._onPointerGone = this._onPointerLeave;

        /**
         * A touch has no hover, so it never generates the stream of pointermove events the
         * steady gust is built on: a tap produced nothing at all. It gets one gust instead,
         * of the same blower, and nothing after it.
         *
         * Only for pointers that are not a mouse, which is per-event rather than per-device on
         * purpose -- on a laptop with a touchscreen the trackpad still hovers and the finger
         * still taps, and a media query would have had to pick one of them.
         */
        this._onPointerDown = (event) => {
            if (event.pointerType === 'mouse') {
                return;
            }

            const rect = this._containerRect();
            const x = event.clientX - (rect ? rect.left : 0);
            const y = event.clientY - (rect ? rect.top : 0);

            if (!config.tap || !config.idle || x < 0 || x > this._width || y < 0 || y > this._height) {
                return;
            }

            // Lifting, because a tap that cannot pick paper up off the drift does nothing
            // visible on a screen where the paper has already landed -- which is most of the
            // time a tap is available at all.
            this._gust(x, y, config.tap, true);
            this._wake();
        };

        /**
         * A finger that drags does generate pointermove, so it blows while it travels -- but
         * lifting it fires no pointerleave, and nothing else was clearing the flag. The gust
         * stayed parked at the last place the finger had been for as long as the loop ran.
         */
        this._onPointerUp = (event) => {
            if (event.pointerType !== 'mouse') {
                this._pointerOn = false;
            }
        };

        host.addEventListener('pointermove', this._onPointerMove, { passive: true });
        host.addEventListener('pointerdown', this._onPointerDown, { passive: true });
        host.addEventListener('pointerup', this._onPointerUp, { passive: true });
        host.addEventListener('pointerleave', this._onPointerLeave, { passive: true });
        host.addEventListener('pointercancel', this._onPointerGone, { passive: true });
        window.addEventListener('blur', this._onPointerGone, { passive: true });
        this._pointerHost = host;
    }

    _unbindPointer() {
        if (!this._pointerHost) {
            return;
        }

        this._pointerHost.removeEventListener('pointermove', this._onPointerMove);
        this._pointerHost.removeEventListener('pointerdown', this._onPointerDown);
        this._pointerHost.removeEventListener('pointerup', this._onPointerUp);
        this._pointerHost.removeEventListener('pointerleave', this._onPointerLeave);
        this._pointerHost.removeEventListener('pointercancel', this._onPointerGone);
        window.removeEventListener('blur', this._onPointerGone);
        this._pointerHost = null;
    }

    _observe() {
        this._bindPointer();
        this._onResize = () => {
            this._rectStale = true;
            this._resize();
        };

        this._onScroll = () => {
            this._rectStale = true;
        };

        // Capture, so a scroll in any ancestor counts, not just the page itself. It only sets
        // a flag; the read itself waits for the next pointer event that actually needs it.
        window.addEventListener('scroll', this._onScroll, { passive: true, capture: true });

        if (this._viewport) {
            window.addEventListener('resize', this._onResize, { passive: true });
            window.addEventListener('orientationchange', this._onResize, { passive: true });
        }
        else if (typeof ResizeObserver === 'function') {
            this._resizeObserver = new ResizeObserver(this._onResize);
            this._resizeObserver.observe(this._container);
        }
        else {
            window.addEventListener('resize', this._onResize, { passive: true });
        }

        if (!this.options.autoPause) {
            return;
        }

        this._onVisibility = () => {
            this._pageVisible = document.visibilityState !== 'hidden';
            this._lastFrame = 0;
        };
        document.addEventListener('visibilitychange', this._onVisibility);

        if (!this._viewport && typeof IntersectionObserver === 'function') {
            this._intersectionObserver = new IntersectionObserver((entries) => {
                this._onScreen = entries[entries.length - 1].isIntersecting;
                this._lastFrame = 0;
            }, { threshold: 0 });
            this._intersectionObserver.observe(this._container);
        }
    }

    /* -------------------------------------------------------------- *
     * Storage — structure of arrays, grown by doubling, never capped
     * -------------------------------------------------------------- */

    _allocate(capacity) {
        const cap = Math.max(capacity, 64);

        if (cap <= this._capacity) {
            return;
        }

        const f32 = (old) => {
            const next = new Float32Array(cap);

            if (old) {
                next.set(old.subarray(0, this._capacity));
            }

            return next;
        };
        const u8 = (old) => {
            const next = new Uint8Array(cap);

            if (old) {
                next.set(old.subarray(0, this._capacity));
            }

            return next;
        };

        this._x = f32(this._x);
        this._y = f32(this._y);
        this._z = f32(this._z);
        this._vx = f32(this._vx);
        this._vy = f32(this._vy);
        this._vz = f32(this._vz);
        this._qx = f32(this._qx);
        this._qy = f32(this._qy);
        this._qz = f32(this._qz);
        this._qw = f32(this._qw);
        this._wx = f32(this._wx);
        this._wy = f32(this._wy);
        this._wz = f32(this._wz);
        this._hw = f32(this._hw);
        this._hh = f32(this._hh);
        this._invChord = f32(this._invChord);
        this._invSpan = f32(this._invSpan);
        this._k = f32(this._k);
        this._age = f32(this._age);
        this._life = f32(this._life);
        this._phase = f32(this._phase);
        this._jet = f32(this._jet);
        this._paper = u8(this._paper);
        this._bounce = u8(this._bounce);

        this._capacity = cap;
    }

    /** Swap-remove. Draw order does not encode anything, so order does not matter. */
    _kill(i) {
        const last = --this._count;

        if (i === last) {
            return;
        }

        const copy = (arr) => {
            arr[i] = arr[last];
        };

        copy(this._x); copy(this._y); copy(this._z);
        copy(this._vx); copy(this._vy); copy(this._vz);
        copy(this._qx); copy(this._qy); copy(this._qz); copy(this._qw);
        copy(this._wx); copy(this._wy); copy(this._wz);
        copy(this._hw); copy(this._hh);
        copy(this._invChord); copy(this._invSpan); copy(this._k);
        copy(this._age); copy(this._life); copy(this._phase); copy(this._jet);
        copy(this._paper); copy(this._bounce);
    }

    /* -------------------------------------------------------------- *
     * Sizing
     * -------------------------------------------------------------- */

    _resize() {
        if (this.destroyed) {
            return;
        }

        const rect = this._viewport
            ? { width: window.innerWidth, height: window.innerHeight }
            : this._container.getBoundingClientRect();

        const width = Math.max(1, Math.round(rect.width));
        const height = Math.max(1, Math.round(rect.height));
        const dpr = Math.min(window.devicePixelRatio || 1, this.options.maxDpr);

        if (width === this._width && height === this._height && dpr === this._dpr) {
            return;
        }

        const scaleX = this._width ? width / this._width : 1;
        const scaleY = this._height ? height / this._height : 1;

        for (let i = 0; i < this._count; i++) {
            this._x[i] *= scaleX;
            this._y[i] *= scaleY;
        }

        for (let s = 0; s < this._settledCount; s++) {
            this._settled[s * RECORD] *= scaleX;
        }

        this._width = width;
        this._height = height;
        this._dpr = dpr;

        [this._canvas, this._pileCanvas].forEach((canvas) => {
            if (!canvas) {
                return;
            }

            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
            canvas.getContext('2d').setTransform(dpr, 0, 0, dpr, 0, 0);
        });

        // Sizing a canvas empties it, so whatever was on the paper layer is gone.
        this._painted = false;

        this._initPile();
    }

    /* -------------------------------------------------------------- *
     * Pile
     * -------------------------------------------------------------- */

    _initPile() {
        const pile = this._pileOptions();

        if (!pile) {
            return;
        }

        this._cell = pile.binSize;
        this._pileBins = Math.max(1, Math.ceil(this._width / this._cell));
        this._gridH = Math.max(1, Math.ceil(this._height / this._cell) + 2);
        this._grid = new Uint8Array(this._pileBins * this._gridH);
        // Cells are 4px but a drift grows by about 1px per piece, so occupancy alone cannot
        // tell a landing piece where to sit — quantised to the cell it would never rise at
        // all. This holds the precise y of the highest paper in each cell.
        this._gridTopY = new Float32Array(this._pileBins * this._gridH).fill(Infinity);
        // Topmost occupied row per column. Derived from the grid, only ever a hint: the
        // landing test reads the grid itself, so a cave is seen even when the column has
        // paper far above it.
        this._gridTop = new Int32Array(this._pileBins).fill(this._gridH);

        if (!this._settled) {
            this._settled = new Float32Array(2048 * RECORD);
            this._settledCount = 0;
        }

        this._rebuildSurface();
        this._redrawPile();
    }

    get _floorY() {
        const pile = this._pileOptions();

        if (!pile || pile.floor === 'bottom') {
            return this._height;
        }

        return pile.floor <= 1 ? pile.floor * this._height : pile.floor;
    }

    /**
     * Half-extents a piece occupies in the drift, written to _fpW / _fpH. Returning them
     * through fields keeps the per-piece surface rebuild allocation-free.
     *
     * NOT the axis-aligned box around the rotated quad: that box holds twice the paper it
     * contains (a 9x14 piece at 45 degrees needs 16x8 for 63px2), and using it stands every
     * piece on its lowest corner and bridges it across its widest span — which is what opens
     * the gaps between settled paper. Shrunk instead to the box with the SAME AREA as the
     * quad, which has a second property worth keeping: the depth one piece deposits then
     * equals its own height exactly, so the modelled drift and the drawn one agree by
     * construction rather than by tuning.
     */
    _footprint(angle, hw, hh) {
        const squash = this._pileOptions().foreshorten;
        const c = Math.abs(Math.cos(angle));
        const s = Math.abs(Math.sin(angle));
        const boxW = c * hw + s * hh;
        const boxH = (s * hw + c * hh) * squash;
        const shrink = Math.sqrt(hw * hh * squash / (boxW * boxH));

        this._fpW = boxW * shrink;
        this._fpH = boxH * shrink;
        // The quad is still DRAWN to the full box height whatever we pack it as, so keep
        // that separately: it is what decides how deep the piece has to sink for its top
        // to come out level with the surface it just raised.
        this._fpDraw = boxH;
    }

    _binOf(x) {
        return clamp(x / this._pileOptions().binSize | 0, 0, this._pileBins - 1);
    }

    /**
     * Column index without clamping. Clamping is right when you need *a* column for a point
     * inside the drift, and wrong when you are walking a footprint: it folds everything that
     * overhangs the container onto the first and last column, so the edges silently collect
     * all the paper that fell past them and grow into walls two to three times the depth of
     * the middle. Paper that misses the container is gone, not stacked against it.
     */
    _rawBin(x) {
        return Math.floor(x / this._pileOptions().binSize);
    }

    /** Screen y of the topmost paper in one column, or the floor when it is empty. */
    _surfaceAt(bin) {
        const top = this._gridTop[bin];

        return top >= this._gridH ? this._floorY : top * this._cell;
    }

    /** Is there paper in the cell holding this point? */
    _occupied(x, y) {
        if (y < 0) {
            return false;
        }

        const row = y / this._cell | 0;

        return row < this._gridH && this._grid[row * this._pileBins + this._binOf(x)] > 0;
    }

    /**
     * Adds (delta 1) or removes (delta -1) one piece from the grid, rasterising the quad it
     * is drawn as. Cells count rather than flag, because pieces overlap and lifting one must
     * not erase the paper its neighbour put in the same cell.
     */
    _markPiece(x, y, angle, hw, hh, delta, limitLo, limitHi) {
        const squash = this._pileOptions().foreshorten;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const ax = cos * hw;
        const ay = sin * hw * squash;
        const bx = -sin * hh;
        const by = cos * hh * squash;
        const det = ax * by - ay * bx;

        if (!det) {
            return;
        }

        // Inverse of the quad's own basis, so a cell centre can be tested with two dots.
        const inv = 1 / det;
        const cell = this._cell;
        const top = y - (Math.abs(ay) + Math.abs(by));
        const reachX = Math.abs(ax) + Math.abs(bx);
        const reachY = Math.abs(ay) + Math.abs(by);
        // A partial rebuild clears a column range and re-marks into it; marking outside that
        // range would count a piece twice, because those cells were never cleared.
        const c0 = Math.max(limitLo === undefined ? 0 : limitLo, (x - reachX) / cell | 0);
        const c1 = Math.min(limitHi === undefined ? this._pileBins - 1 : limitHi, (x + reachX) / cell | 0);
        const r0 = Math.max(0, (y - reachY) / cell | 0);
        const r1 = Math.min(this._gridH - 1, (y + reachY) / cell | 0);

        for (let r = r0; r <= r1; r++) {
            const py = (r + 0.5) * cell - y;

            for (let cIndex = c0; cIndex <= c1; cIndex++) {
                const px = (cIndex + 0.5) * cell - x;
                const alpha = (px * by - py * bx) * inv;

                if (alpha < -1 || alpha > 1) {
                    continue;
                }

                const beta = (ax * py - ay * px) * inv;

                if (beta < -1 || beta > 1) {
                    continue;
                }

                const k = r * this._pileBins + cIndex;

                if (delta > 0) {
                    if (this._grid[k] < 255) {
                        this._grid[k]++;
                    }

                    if (top < this._gridTopY[k]) {
                        this._gridTopY[k] = top;
                    }

                    if (r < this._gridTop[cIndex]) {
                        this._gridTop[cIndex] = r;
                    }
                }
                else if (this._grid[k]) {
                    this._grid[k]--;
                }
            }
        }
    }

    /** Re-derives the top hint for one column after paper was taken out of it. */
    _recomputeTop(col) {
        const bins = this._pileBins;

        for (let r = 0; r < this._gridH; r++) {
            if (this._grid[r * bins + col]) {
                this._gridTop[col] = r;

                return;
            }
        }

        this._gridTop[col] = this._gridH;
    }

    /**
     * Where a piece falling at `x` from `fromY` first meets paper, or the floor. Walking the
     * grid is what lets it drop through a hollow and land on the paper at the bottom of it,
     * instead of stopping on a surface that a column height merely claims is there.
     */
    _dropTo(x, fromY) {
        const cell = this._cell;
        const floor = this._floorY;
        const col = this._binOf(x);
        let y = fromY;

        while (y < floor) {
            const row = y / cell | 0;

            if (row >= 0 && row < this._gridH && this._grid[row * this._pileBins + col]) {
                // Land on the paper itself, not on the cell that happens to contain it.
                return Math.min(this._gridTopY[row * this._pileBins + col], floor);
            }

            y += cell;
        }

        return floor;
    }

    /** How deep the paper reaches under a footprint, in px above the floor. */
    _depthUnder(x, halfW) {
        const lo = Math.max(0, this._rawBin(x - halfW));
        const hi = Math.min(this._pileBins - 1, this._rawBin(x + halfW));
        const floor = this._floorY;
        let deepest = 0;

        for (let b = lo; b <= hi; b++) {
            const depth = floor - this._surfaceAt(b);

            if (depth > deepest) {
                deepest = depth;
            }
        }

        return deepest;
    }

    /**
     * Rebuilds the grid from the pieces actually remembered. Given a column range, only those
     * columns are cleared and re-marked; every other cell keeps the count it already had.
     *
     * A gust only changes the columns it reaches, but rebuilding all of them costs all of
     * them — measured at 3.2ms for 7056 pieces, and that ran on every pointermove, not once
     * per frame. The resulting grid is identical.
     */
    _rebuildSurface(colLo, colHi, listed) {
        if (!this._grid) {
            return;
        }

        const bins = this._pileBins;
        const partial = colLo !== undefined;
        const lo = partial ? Math.max(0, colLo) : 0;
        const hi = partial ? Math.min(bins - 1, colHi) : bins - 1;

        if (hi < lo) {
            return;
        }

        if (partial) {
            for (let r = 0; r < this._gridH; r++) {
                const row = r * bins;

                for (let col = lo; col <= hi; col++) {
                    this._grid[row + col] = 0;
                    this._gridTopY[row + col] = Infinity;
                }
            }

            for (let col = lo; col <= hi; col++) {
                this._gridTop[col] = this._gridH;
            }
        }
        else {
            this._grid.fill(0);
            this._gridTopY.fill(Infinity);
            this._gridTop.fill(this._gridH);
        }

        const data = this._settled;
        const floor = this._floorY;
        const cell = this._cell;
        // The caller already walked every record to remove the lifted ones, and noted which of
        // the survivors reach these columns. Walking them all over again to find the same set
        // is the second of three full passes a single gust used to cost.
        const list = listed === undefined ? null : this._touched;
        const count = list ? listed : this._settledCount;

        for (let k = 0; k < count; k++) {
            const s = list ? list[k] : k;
            const o = s * RECORD;

            if (partial && !list) {
                const reach = data[o + 3] + data[o + 4];

                if ((data[o] + reach) / cell < lo || (data[o] - reach) / cell > hi) {
                    continue;
                }
            }

            this._markPiece(
                data[o], floor - data[o + 1], data[o + 2], data[o + 3], data[o + 4], 1,
                partial ? lo : undefined, partial ? hi : undefined,
            );
        }
    }

    /** Notes a region of the pile canvas as needing repainting on the next frame. */
    _markDirty(x0, y0, x1, y1) {
        if (this._pileDirty) {
            this._dirtyX0 = Math.min(this._dirtyX0, x0);
            this._dirtyY0 = Math.min(this._dirtyY0, y0);
            this._dirtyX1 = Math.max(this._dirtyX1, x1);
            this._dirtyY1 = Math.max(this._dirtyY1, y1);
        }
        else {
            this._dirtyX0 = x0;
            this._dirtyY0 = y0;
            this._dirtyX1 = x1;
            this._dirtyY1 = y1;
            this._pileDirty = true;
        }
    }

    /**
     * Repaints the settled paper. Given a rectangle, only that part is cleared and redrawn,
     * clipped to it so a quad hanging outside cannot paint over what is already there.
     *
     * A gust disturbs a strip a couple of piece-widths across, but repainting the whole
     * drift costs the whole drift: measured at 13.3ms for 7056 pieces, once per frame while
     * the pointer is down, against a 16.7ms budget. The pixels come out identical either way.
     */
    _redrawPile(x0, y0, x1, y1) {
        const ctx = this._pileCtx;

        if (!ctx) {
            return;
        }

        const partial = x0 !== undefined;
        // Snap outward to whole device pixels. A clip edge that falls mid-pixel antialiases
        // against it, so the seam would come out a shade different from a full repaint —
        // measured at 418 bytes of 1.7M before snapping, invisible but not identical.
        const dpr = this._dpr;
        const snapLo = (v) => Math.floor(v * dpr) / dpr;
        const snapHi = (v) => Math.ceil(v * dpr) / dpr;
        const lo = partial ? Math.max(0, snapLo(x0)) : 0;
        const hi = partial ? Math.min(this._width, snapHi(x1)) : this._width;
        const top = partial ? Math.max(0, snapLo(y0)) : 0;
        const bottom = partial ? Math.min(this._height, snapHi(y1)) : this._height;

        if (hi <= lo || bottom <= top) {
            return;
        }

        ctx.save();
        ctx.beginPath();
        ctx.rect(lo, top, hi - lo, bottom - top);
        ctx.clip();
        ctx.clearRect(lo, top, hi - lo, bottom - top);

        const floor = this._floorY;
        const data = this._settled;

        for (let s = 0; s < this._settledCount; s++) {
            const o = s * RECORD;

            if (partial) {
                // hw + hh is never smaller than the rotated quad half-extent on either axis,
                // so this skips only pieces that certainly do not reach the rectangle.
                const reach = data[o + 3] + data[o + 4];
                const cx = data[o];
                const cy = floor - data[o + 1];

                if (cx + reach < lo || cx - reach > hi || cy + reach < top || cy - reach > bottom) {
                    continue;
                }
            }

            this._paintSettled(
                data[o], floor - data[o + 1], data[o + 2], data[o + 3], data[o + 4],
                this._papers[data[o + 5]] || this._papers[0], data[o + 6], data[o + 7],
            );
        }

        ctx.restore();
    }

    /**
     * Paints one piece of settled paper, shaded by the light as it stands at this moment.
     *
     * The shade used to be worked out once, when the piece landed, and stored. That froze it:
     * turning ambient or albedo afterwards did nothing to paper already on the floor, and the
     * sheen term was missing from the formula altogether, so that slider could never reach the
     * drift at all. What the record keeps now is how squarely the piece faces the light, which
     * is a property of the piece; the colour that comes out of it is a property of the light,
     * and is recomputed here. Change the light and repaint, and the drift follows.
     */
    _paintSettled(x, y, angle, hw, hh, paper, side, facing) {
        const ctx = this._pileCtx;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const squash = this._pileOptions().foreshorten;
        const ax = cos * hw;
        const ay = sin * hw * squash;
        const bx = -sin * hh;
        const by = cos * hh * squash;

        const r = this.options.render;
        // The same terms the airborne pieces get, so a piece does not change appearance at the
        // moment it lands: diffuse by how it faces the light, plus a narrow specular highlight
        // on the few that face it almost squarely.
        const f2 = facing * facing;
        const f4 = f2 * f2;
        const f8 = f4 * f4;
        const shade = r.ambient + paper.albedo * r.diffuse * facing + r.specular * f8 * f8;

        ctx.fillStyle = this._shadeColor(paper, side, shade);
        ctx.beginPath();
        ctx.moveTo(x - ax - bx, y - ay - by);
        ctx.lineTo(x + ax - bx, y + ay - by);
        ctx.lineTo(x + ax + bx, y + ay + by);
        ctx.lineTo(x - ax + bx, y - ay + by);
        ctx.fill();
    }

    /** Appends one piece to the record list, growing it by doubling. */
    _remember(x, aboveFloor, angle, hw, hh, paperIndex, side, facing) {
        if ((this._settledCount + 1) * RECORD > this._settled.length) {
            const next = new Float32Array(this._settled.length * 2);

            next.set(this._settled);
            this._settled = next;
        }

        const o = this._settledCount++ * RECORD;
        const data = this._settled;

        data[o] = x;
        data[o + 1] = aboveFloor;
        data[o + 2] = angle;
        data[o + 3] = hw;
        data[o + 4] = hh;
        data[o + 5] = paperIndex;
        data[o + 6] = side;
        data[o + 7] = facing;
    }

    /**
     * Where a piece landing at `x` actually comes to rest. Paper on a slope slides down it,
     * so this trades height gained against distance travelled and takes the cheapest spot.
     */
    _restingPlace(x, halfW, halfH) {
        const pile = this._pileOptions();

        if (!pile.repose) {
            return x;
        }

        const binSize = this._cell;
        // Noise the size of the piece itself, for two reasons. Taking the strict minimum
        // funnels every piece landing nearby into one column until it stops being the
        // lowest, so the drift fills column by column; and snapping the result to a bin
        // centre lines those columns up on a 4px grid. Together they draw a visible
        // vertical stripe pattern through the settled paper. Real paper does not pick the
        // optimum, so choose loosely between near-equal columns and land anywhere in one.
        const noise = 2 * halfH;
        let best = x;
        // Cost is DEPTH gained plus distance travelled. Comparing screen y instead would
        // invert it — a smaller y means deeper paper — and send every piece to the top of
        // the heap rather than off it. Scanning every column lets paper flow as far as the
        // slope justifies, which is what levels a drift instead of stacking a needle.
        let bestCost = this._depthUnder(x, halfW);

        for (let b = 0; b < this._pileBins; b++) {
            const bx = (b + 0.5) * binSize;
            const cost = this._floorY - this._surfaceAt(b) + pile.repose * Math.abs(bx - x) + Math.random() * noise;

            if (cost < bestCost) {
                bestCost = cost;
                best = bx;
            }
        }

        // Scatter across a piece width, whether it slid or not. The drift is modelled in
        // columns and nothing mixes paper sideways, so every spot that keeps receiving
        // pieces builds a vertical chain of them — which is the stripe pattern you see
        // through a deep drift. Measured over 5000 settled pieces, scattering this far
        // cuts the clustering of piece centres from 0.39 to 0.29 and costs nothing.
        const scattered = best + rand(-halfW * 2, halfW * 2);

        // Reflect rather than clamp. Clamping parks every piece that would overshoot exactly
        // on the boundary, where half its quad is clipped away, and they stack up into a line
        // of half-visible paper along the edge — measured at 31 of them after a few sweeps.
        if (scattered < 0) {
            return -scattered;
        }

        return scattered > this._width ? 2 * this._width - scattered : scattered;
    }

    /** Lays a piece on the drift: remembered, painted, and the surface raised to match. */
    _settle(i) {
        const hw = this._hw[i];
        const hh = this._hh[i];
        const angle = Math.random() * TAU;

        this._footprint(angle, hw, hh);

        const halfW = this._fpW;
        const drawn = this._fpDraw;
        const pile = this._pileOptions();

        // Sliding down a slope is something paper does on the surface. A piece that came to
        // rest INSIDE the drift -- a gust dropped it back into the hollow it just made -- has
        // paper packed all around it and cannot travel sideways through it.
        //
        // Letting it travel anyway is what packs a heap solid. _dropTo scans DOWNWARDS from
        // wherever it starts, and the clamp below keeps a piece from rising, so a buried piece
        // carried its own depth into a different column and was stacked into cells that
        // already held paper -- and cells count without a ceiling, so nothing stopped it.
        // Measured with the pointer resting in a heap: paper under it packed 4.2x denser and a
        // 47px drift read as a 10px trench with the same pieces still in it.
        const buried = this._y[i] > this._surfaceAt(this._binOf(this._x[i])) + drawn;
        const x = buried ? this._x[i] : this._restingPlace(this._x[i], halfW, this._fpH);

        // A buried piece looks for support one cell DOWN rather than where it stands. Its own
        // cell holds paper whenever it is merely beside some, which is true of everything a
        // gust releases inside a drift: the paper that was holding it up has gone, its
        // neighbours at its own height have not, and without this it stops in the air and the
        // hollow underneath never closes.
        const from = buried ? this._y[i] + this._cell : this._y[i];
        // The piece already stopped where it met paper, so start from there. If repose moved
        // it sideways it may now be over a lower spot, so let it drop again — through the
        // grid, which means it falls into a hollow rather than hanging over one.
        //
        // Then nestle: paper is floppy and works its way into what is under it rather than
        // perching on top, and without that the quads only touch and leave gaps between them.
        // It can only ever move the piece DOWN, so nothing rises on settling.

        // Where the paper says, or where the piece actually stopped — whichever is lower.
        // _dropTo reports the top of the paper anywhere in the 4px cell it hit, and that edge
        // is often above the piece itself, so without the clamp settling lifts it: measured on
        // 60% of landings when dropped and 76% when fired, by a median of 1.7 and 3.5px. That
        // lift accumulates up the stack and partly cancels the nestling, which is why a heap
        // came out about twice as tall as its own nestle setting asks for. Want taller heaps
        // after this: lower nestle, which is the knob for it.
        const y = Math.min(Math.max(this._dropTo(x, from) - drawn * (1 - pile.nestle), this._y[i]), this._floorY);

        const paperIndex = this._paper[i];
        const paper = this._papers[paperIndex] || this._papers[0];
        const side = Math.random() < 0.5 ? 0 : 1;
        // How squarely this piece ends up facing the light. Paper on the floor is tilted every
        // which way, not mirror-flat, and taking the top of the range for all of them bleaches
        // the whole drift white.
        const facing = rand(0.55, 0.92);

        this._remember(x, this._floorY - y, angle, hw, hh, paperIndex, side, facing);
        this._paintSettled(x, y, angle, hw, hh, paper, side, facing);
        this._markPiece(x, y, angle, hw, hh, 1);

        if (this.options.onSettle) {
            this.options.onSettle(x, y);
        }

        this._kill(i);
    }

    /** Removes the pile and lets it start over. */
    clearPile() {
        if (!this._pileCtx) {
            return this;
        }

        this._settledCount = 0;

        if (this._grid) {
            this._grid.fill(0);
            this._gridTopY.fill(Infinity);
            this._gridTop.fill(this._gridH);
        }

        this._pileCtx.clearRect(0, 0, this._width, this._height);

        return this;
    }

    /**
     * Calls back once when a piece first rises past `y`, a fraction of the stage. Nothing
     * guarantees one ever does, so a caller that must act either way keeps its own fallback.
     */
    onceAboveY(y, callback) {
        this._crestY = y * this._height;
        this._crestWatchers.push(callback);

        return this;
    }

    _crest() {
        const watchers = this._crestWatchers.slice();

        this._crestWatchers.length = 0;
        watchers.forEach((watcher) => watcher());
    }

    /** Restarts the drift's clock, so the countdown runs from the last emission, not the first. */
    _armPileFade() {
        this._finishPileFade();

        const seconds = this.options.pileLife && this.options.pileLife.seconds;

        if (!seconds || !this._pileOptions()) {
            return;
        }

        this._pileTimer = setTimeout(() => this._fadePile(), seconds * 1000);
    }

    _cancelPileFade() {
        if (this._pileTimer) {
            clearTimeout(this._pileTimer);
            this._pileTimer = 0;
        }

        if (this._pileFadeTimer) {
            clearTimeout(this._pileFadeTimer);
            this._pileFadeTimer = 0;
        }
    }

    /** Cancels the wait, and finishes a fade already under way rather than reviving that paper. */
    _finishPileFade() {
        const fading = !!this._pileFadeTimer;

        this._cancelPileFade();

        if (fading) {
            this.clearPile();
            this._pileCanvas.style.transition = '';
            this._pileCanvas.style.opacity = '';
        }
    }

    /** Fades the settled paper out, then drops it and puts the canvas back as it was. */
    _fadePile() {
        this._pileTimer = 0;

        if (!this._pileCanvas) {
            return;
        }

        const fade = Math.max(0, this.options.pileLife.fade);
        const canvas = this._pileCanvas;

        canvas.style.transition = `opacity ${ fade }s linear`;
        canvas.style.opacity = '0';

        this._pileFadeTimer = setTimeout(() => {
            this._pileFadeTimer = 0;
            this.clearPile();

            // Same canvas, emptied: taking the transition off first stops the next pile
            // fading back in over paper that is already on the floor.
            canvas.style.transition = '';
            canvas.style.opacity = '';
        }, fade * 1000);
    }

    /* -------------------------------------------------------------- *
     * Emission
     * -------------------------------------------------------------- */

    /**
     * A preset may name instance options, so that one name is the whole effect rather than
     * most of it. configure() owns every switch: it rebuilds the pile canvas and its occupancy
     * grid, and rebinds the pointer. Only what actually differs is passed on, because those
     * rebuilds are not free.
     */
    _applyEffectOptions(effect, given) {
        const wanted = Object.assign({}, effect.options, pickDefined(given || {}, EFFECT_OPTIONS));
        const changed = Object.keys(wanted).filter((key) => wanted[key] !== this.options[key]);

        if (changed.length) {
            this.configure(pickDefined(wanted, changed));
        }
    }

    /** One-shot burst. `spec` is a preset name, a spec object, or an array of either. */
    burst(spec = 'burst', overrides) {
        const effect = unwrapEffect(spec);

        this._applyEffectOptions(effect, overrides);

        resolveSpec(effect.emit, overrides, effect.fromPreset, effect.overrides)
            .forEach((one) => this._spawn(one, one.count));
        this._armPileFade();
        this._wake();

        return this;
    }

    /**
     * Continuous emission.
     * @param {Object|string} spec A preset name, or
     *                        `{ preset?, rate?, duration?, pile?, ...emitOverrides }`.
     *                        rate is pieces per second; duration in ms (0 = until stopped).
     *                        Whatever the caller sets here beats what the preset carries.
     */
    stream(spec = {}) {
        const given = typeof spec === 'string' ? { preset: spec } : spec;
        const effect = unwrapEffect(given.preset || given.emit || {});
        const specs = resolveSpec(effect.emit, given, effect.fromPreset, effect.overrides);
        const rate = firstDefined(given.rate, effect.rate, STREAM_DEFAULTS.rate);
        const duration = firstDefined(given.duration, effect.duration, STREAM_DEFAULTS.duration);

        this._applyEffectOptions(effect, given);

        const handle = {
            specs,
            rate: this._reduced ? rate * 0.45 : rate,
            remaining: duration > 0 ? duration / 1000 : Infinity,
            carry: 0,
        };

        this._streams.push(handle);
        this._cancelPileFade();
        this._wake();

        return handle;
    }

    /** Stops emitting. `{ clear: true }` also removes pieces already in flight. */
    stop({ clear = false } = {}) {
        const wasStreaming = this._streams.length > 0;

        this._streams.length = 0;

        if (wasStreaming) {
            this._armPileFade();
        }

        if (clear) {
            this._count = 0;
            this._ctx.clearRect(0, 0, this._width, this._height);
            this._painted = false;
        }

        return this;
    }

    _pickPaper(allowed) {
        if (allowed) {
            return allowed[Math.random() * allowed.length | 0];
        }

        const roll = Math.random() * this._shareTotal;
        const cum = this._shareCum;

        for (let p = 0; p < cum.length; p++) {
            if (roll < cum[p]) {
                return p;
            }
        }

        return cum.length - 1;
    }

    _resolvePaperFilter(spec) {
        if (!spec.papers) {
            return null;
        }

        const list = (Array.isArray(spec.papers) ? spec.papers : [spec.papers])
            .map((entry) => typeof entry === 'number'
                ? entry
                : this._papers.findIndex((paper) => paper.color === entry))
            .filter((index) => index >= 0 && index < this._papers.length);

        return list.length ? list : null;
    }

    /** How big the stage is along `axis` against the one an emission's numbers were chosen at. */
    _stageRatio(axis) {
        return axis === 'width'
            ? this._width / 1600
            : this._height / 900;
    }

    /** Container-relative multiplier for preset launch speeds and jets. */
    _emitScale(spec) {
        const configured = this.options.emitScale;

        if (typeof configured === 'number') {
            return configured;
        }

        return Math.sqrt(clamp(this._stageRatio(spec && spec.scaleAxis), 0.2, 2.2));
    }

    /** Container-relative multiplier for the amount of paper; floored well above the speed clamp. */
    _countScale(spec) {
        if (!spec || !spec.scaleCount) {
            return 1;
        }

        const axis = typeof spec.scaleCount === 'string' ? spec.scaleCount : spec.scaleAxis;

        return clamp(this._stageRatio(axis), 0.35, 1.6);
    }

    _spawn(spec, count) {
        // Carried, not rounded: a stream arrives a piece or two at a time, and a scaled down
        // tick that rounds to zero would emit nothing at all.
        const wanted = (this._reduced ? count * 0.45 : count) * this._countScale(spec)
            + (spec._countCarry || 0);
        const total = Math.max(0, Math.floor(wanted));

        spec._countCarry = wanted - total;

        if (!total) {
            return;
        }

        this._allocate(Math.max(this._capacity, this._count + total));

        const W = this._width;
        const H = this._height;
        const speed = range(spec.speed, EMIT_DEFAULTS.speed);
        const spin = range(spec.spin, EMIT_DEFAULTS.spin);
        const depth = range(spec.depth, EMIT_DEFAULTS.depth);
        const depthSpeed = range(spec.depthSpeed, EMIT_DEFAULTS.depthSpeed);
        const stage = this._emitScale(spec);
        const slow = (this._reduced ? 0.6 : 1) * (spec.scaleSpeed === false ? 1 : stage);
        const jetSeconds = (spec.jetSeconds === null || spec.jetSeconds === undefined
            ? this.options.physics.launchJet.seconds
            : spec.jetSeconds) * stage;
        const cone = clamp(spec.spreadAngle, 0, 360) * DEG;
        const centre = spec.angle * DEG;
        const originX = spec.originPx ? spec.originPx.x : spec.origin.x * W;
        const originY = spec.originPx ? spec.originPx.y : spec.origin.y * H;
        const boxX = spec.box.x * W;
        const boxY = spec.box.y * H;
        const allowed = this._resolvePaperFilter(spec);
        const autoLife = this.options.life.seconds > 0
            ? this.options.life.seconds
            : clamp(H / 110 + 2.2, 2.5, this.options.life.max);

        for (let n = 0; n < total; n++) {
            const p = this._pickPaper(allowed);
            const paper = this._papers[p] || this._papers[0];
            const scale = 1 + rand(-paper.variation, paper.variation);
            const a = centre + rand(-cone * 0.5, cone * 0.5);
            const v = rand(speed[0], speed[1]) * slow;
            // v_term = sqrt(g / (k * C_N)) — the same closed form the readouts use.
            const terminal = Math.sqrt(this.options.physics.gravity / (paper.k * this.options.physics.aero.normal));

            this._inject(
                p,
                Math.max(0.4, paper.width * scale * 0.5),
                Math.max(0.4, paper.height * scale * 0.5),
                originX + rand(-boxX * 0.5, boxX * 0.5),
                originY + rand(-boxY * 0.5, boxY * 0.5),
                rand(depth[0], depth[1]),
                Math.cos(a) * v,
                -Math.sin(a) * v,
                rand(depthSpeed[0], depthSpeed[1]),
                rand(spin[0], spin[1]) * slow,
                v > terminal * 1.6 ? jetSeconds : 0,
                autoLife,
            );
        }
    }

    /**
     * Places one piece in an explicit state and returns its index. Shared by the emitters
     * and by blow(), which relaunches settled pieces through it. Positional arguments on
     * purpose: a burst calls this a thousand times at once and has no business allocating
     * an options object per piece.
     */
    _inject(p, hw, hh, x, y, z, vx, vy, vz, omega, jetSeconds, autoLife) {
        if (this._count >= this._capacity) {
            this._allocate(this._capacity * 2);
        }

        const i = this._count++;
        const paper = this._papers[p] || this._papers[0];
        const life = autoLife === undefined
            ? clamp(this._height / 110 + 2.2, 2.5, this.options.life.max)
            : autoLife;

        this._x[i] = x;
        this._y[i] = y;
        this._z[i] = z;
        this._vx[i] = vx;
        this._vy[i] = vy;
        this._vz[i] = vz;

        // Uniformly distributed random orientation (Shoemake's method).
        const u1 = Math.random();
        const u2 = Math.random() * TAU;
        const u3 = Math.random() * TAU;
        const s1 = Math.sqrt(1 - u1);
        const s2 = Math.sqrt(u1);

        this._qx[i] = s1 * Math.sin(u2);
        this._qy[i] = s1 * Math.cos(u2);
        this._qz[i] = s2 * Math.sin(u3);
        this._qw[i] = s2 * Math.cos(u3);

        const dirZ = rand(-1, 1);
        const dirR = Math.sqrt(Math.max(0, 1 - dirZ * dirZ));
        const dirA = Math.random() * TAU;

        this._wx[i] = Math.cos(dirA) * dirR * omega;
        this._wy[i] = Math.sin(dirA) * dirR * omega;
        this._wz[i] = dirZ * omega;

        this._hw[i] = hw;
        this._hh[i] = hh;
        this._invChord[i] = 0.5 / hw;
        this._invSpan[i] = 0.5 / hh;
        this._k[i] = paper.k;
        this._age[i] = 0;
        this._life[i] = this._pileOptions() ? this.options.life.max : life * rand(0.85, 1.2);
        this._phase[i] = Math.random() * TAU;
        this._jet[i] = jetSeconds;
        this._paper[i] = p;
        this._bounce[i] = 0;

        return i;
    }

    /**
     * A gust of air at one point: shoves pieces in flight and lifts settled ones back into
     * the air rather than deleting them.
     *
     * @param {number} x Local x, in css px.
     * @param {number} y Local y, in css px.
     * @param {Object} [options] `{ radius, strength, dirX, dirY, lift, carryAway }` — dirX/dirY
     *                 bias the gust along a direction, such as the pointer's travel, and
     *                 carryAway is the chance each piece it lifts is taken out of the scene.
     */
    blow(x, y, options = {}) {
        const radius = options.radius === undefined ? 110 : options.radius;
        const strength = options.strength === undefined ? 700 : options.strength;
        const dirX = options.dirX || 0;
        const dirY = options.dirY || 0;
        const lift = options.lift === undefined ? 0.5 : options.lift;
        const r2 = radius * radius;

        if (radius <= 0 || strength <= 0) {
            return this;
        }

        for (let i = 0; i < this._count; i++) {
            const dx = this._x[i] - x;
            const dy = this._y[i] - y;
            const d2 = dx * dx + dy * dy;

            if (d2 > r2) {
                continue;
            }

            const distance = Math.sqrt(d2);
            const falloff = 1 - distance / radius;
            const gust = strength * falloff * falloff;

            shapeGust(dx, dy, distance, radius, lift);

            this._vx[i] += (gustX + dirX) * gust;
            // gustY is never positive, so a gust can only ever lift.
            this._vy[i] += (gustY + dirY) * gust;
            this._wz[i] += rand(-9, 9) * falloff;
        }

        // Skipped on frames that are not one of the sampling moments: see interactive.liftRate.
        if (options.lifting !== false) {
            this._scour(x, y, radius, strength, dirX, dirY, lift, options.carryAway || 0);
        }

        return this;
    }

    /**
     * Lifts settled pieces inside the gust back into flight, compacting the record list.
     * Only paper near the surface of its column is loose enough for air to catch; testing
     * the radius alone would relaunch buried pieces too and the heap would visibly jump.
     */
    _scour(x, y, radius, strength, dirX, dirY, lift, carryAway) {
        const settled = this._settled;

        if (!settled || !this._settledCount || !this._grid) {
            return;
        }

        const floor = this._floorY;
        const r2 = radius * radius;
        const jetSeconds = this.options.physics.launchJet.seconds * 0.6;

        // The columns the rebuild afterwards will clear, known before the walk because they
        // depend only on where the gust is. Records that reach them are noted on the way past,
        // so the rebuild does not have to walk every record a second time to find them.
        const margin = 32;
        const cell = this._cell;
        const colLo = ((x - radius - margin) / cell | 0) - 1;
        const colHi = ((x + radius + margin) / cell | 0) + 1;

        if (!this._touched || this._touched.length < this._settledCount) {
            this._touched = new Int32Array(Math.max(256, this._settledCount * 2));
        }

        const touched = this._touched;
        let found = 0;
        let write = 0;
        let lifted = 0;
        // Vertical span of what the gust actually took, so the repaint covers that and not the
        // full height of the stage: the drift is a small fraction of it.
        let takenTop = Infinity;
        let takenBottom = -Infinity;

        for (let s = 0; s < this._settledCount; s++) {
            const o = s * RECORD;
            const sx = settled[o];
            const sy = floor - settled[o + 1];
            const dx = sx - x;
            // How deep the gust reaches in this column. Everything at or above that goes:
            // paper inside the gust because the air takes it, paper over it because it was
            // resting on what just left. Taking only what lies inside the circle is what
            // leaves settled pieces stranded in mid-air over a hollow, and pairs with a
            // near-surface test that made sweeping deeper either do nothing or pick off
            // whatever happened to be shallow nearby.
            const reach = dx < -radius || dx > radius ? -1 : Math.sqrt(r2 - dx * dx);

            if (reach < 0 || sy > y + reach) {
                if (write !== s) {
                    settled.copyWithin(write * RECORD, o, o + RECORD);
                }

                // Noted at its position AFTER compaction, which is where the rebuild will
                // look for it. hw + hh is never smaller than the rotated quad half-extent, so
                // this can only ever include a record too many, never miss one.
                const span = settled[o + 3] + settled[o + 4];

                if ((sx + span) / cell >= colLo && (sx - span) / cell <= colHi) {
                    touched[found++] = write;
                }

                write++;
                continue;
            }

            const dy = sy - y;
            const d2 = dx * dx + dy * dy;

            if (d2 <= r2) {
                const distance = Math.sqrt(d2);
                const falloff = 1 - distance / radius;
                const gust = strength * falloff;

                shapeGust(dx, dy, distance, radius, lift);

                const index = this._inject(
                    settled[o + 5],
                    settled[o + 3],
                    settled[o + 4],
                    sx,
                    sy,
                    rand(-70, 70),
                    (gustX + dirX) * gust + rand(-50, 50),
                    (gustY - rand(0.1, 0.6) + dirY) * gust - rand(30, 140),
                    rand(-40, 40),
                    rand(5, 24),
                    jetSeconds,
                );

                // Carried off by the brush: thrown clear, held out of the landing test so it
                // cannot rejoin the drift, and faded out on the way rather than deleted where
                // it lay. A long jet also means it keeps its speed, so it actually leaves.
                if (carryAway > 0 && index >= 0 && Math.random() < carryAway) {
                    this._vx[index] += dirX * 340 + rand(-60, 60);
                    this._vy[index] -= 220 + rand(0, 160);
                    this._jet[index] = 10;
                    this._life[index] = 0.9;
                }
            }
            else {
                // Not in the gust, just no longer supported: let it drop into the hollow
                // rather than fly, so the drift closes over the trench instead of erupting.
                this._inject(
                    settled[o + 5],
                    settled[o + 3],
                    settled[o + 4],
                    sx,
                    sy,
                    rand(-70, 70),
                    dirX * strength * 0.1 + rand(-25, 25),
                    rand(0, 50),
                    rand(-20, 20),
                    rand(2, 9),
                    0,
                );
            }

            if (sy < takenTop) {
                takenTop = sy;
            }

            if (sy > takenBottom) {
                takenBottom = sy;
            }

            lifted++;
        }

        if (!lifted) {
            return;
        }

        this._settledCount = write;
        this._touchedCount = found;

        // A gust takes paper from its own radius and everything above it in those columns, so
        // that strip, full height, is all that can have changed. The margin covers pieces
        // whose quad straddles the edge of it.
        this._rebuildSurface(colLo, colHi, found);
        this._markDirty(x - radius - margin, takenTop - margin, x + radius + margin, takenBottom + margin);
        this._wake();
    }

    /* -------------------------------------------------------------- *
     * Simulation
     * -------------------------------------------------------------- */

    _step(dt) {
        const P = this.options.physics;
        const A = P.aero;
        const gravity = P.gravity;
        const windX = P.wind.x;
        const windY = P.wind.y;
        const turbA = this._reduced ? P.turbulence.amplitude * 0.4 : P.turbulence.amplitude;
        const turbF = P.turbulence.frequency;
        const jetDrag = P.launchJet.drag;
        // 0 means no wall, rather than a wall at zero -- which would flatten depth completely.
        const depthWall = P.depthLimit > 0 ? P.perspective * P.depthLimit : Infinity;
        const maxSpin = P.maxSpin;
        const maxSpeed = P.maxSpeed;
        const pile = this._pileOptions();
        const floor = pile ? this._floorY : 0;
        const walls = this.options.walls;
        const wallBounce = typeof walls === 'number' ? clamp(walls, 0, 1) : 0.5;
        const margin = 160;
        const W = this._width;
        const H = this._height;
        const halfDt = dt * 0.5;

        this._time += dt;

        const t = this._time;
        let i = 0;

        while (i < this._count) {
            const qx = this._qx[i];
            const qy = this._qy[i];
            const qz = this._qz[i];
            const qw = this._qw[i];

            // Body axes in world space: columns of the rotation matrix.
            const xx = qx * qx;
            const yy = qy * qy;
            const zz = qz * qz;
            const xy = qx * qy;
            const xz = qx * qz;
            const yz = qy * qz;
            const wx2 = qw * qx;
            const wy2 = qw * qy;
            const wz2 = qw * qz;

            const e1x = 1 - 2 * (yy + zz);
            const e1y = 2 * (xy + wz2);
            const e1z = 2 * (xz - wy2);
            const e2x = 2 * (xy - wz2);
            const e2y = 1 - 2 * (xx + zz);
            const e2z = 2 * (yz + wx2);
            const nx = 2 * (xz + wy2);
            const ny = 2 * (yz - wx2);
            const nz = 1 - 2 * (xx + yy);

            // Velocity relative to the air, including this piece's own eddy.
            const gust = turbA ? turbA * fastSin(t * turbF + this._phase[i]) : 0;
            const rvx = this._vx[i] - windX - gust;
            const rvy = this._vy[i] - windY - gust * 0.25;
            const rvz = this._vz[i];

            const vn = rvx * nx + rvy * ny + rvz * nz;
            const v1 = rvx * e1x + rvy * e1y + rvz * e1z;
            const v2 = rvx * e2x + rvy * e2y + rvz * e2z;

            const jet = this._jet[i];
            const age = this._age[i];
            // Inside the launch jet the piece is still travelling with its own air.
            const k = jet > age ? this._k[i] * (jetDrag + (1 - jetDrag) * (age / jet)) : this._k[i];

            // Pressure on the face — the dominant term.
            const kn = k * A.normal * (vn < 0 ? -vn : vn);
            let ax = -kn * vn * nx;
            let ay = -kn * vn * ny + gravity;
            let az = -kn * vn * nz;

            // Skin friction along the face.
            const tx = rvx - vn * nx;
            const ty = rvy - vn * ny;
            const tz = rvz - vn * nz;
            const kt = k * A.tangent * Math.sqrt(tx * tx + ty * ty + tz * tz);

            ax -= kt * tx;
            ay -= kt * ty;
            az -= kt * tz;

            // Rotational (Magnus-like) lift: the term that makes paper dart sideways.
            const wxi = this._wx[i];
            const wyi = this._wy[i];
            const wzi = this._wz[i];
            const kr = k * A.rotational * (this._hw[i] + this._hw[i]);

            ax += kr * (wyi * rvz - wzi * rvy);
            ay += kr * (wzi * rvx - wxi * rvz);
            az += kr * (wxi * rvy - wyi * rvx);

            let vx = this._vx[i] + ax * dt;
            let vy = this._vy[i] + ay * dt;
            let vz = this._vz[i] + az * dt;

            const speed2 = vx * vx + vy * vy + vz * vz;

            if (speed2 > maxSpeed * maxSpeed) {
                const trim = maxSpeed / Math.sqrt(speed2);

                vx *= trim;
                vy *= trim;
                vz *= trim;
            }

            this._vx[i] = vx;
            this._vy[i] = vy;
            this._vz[i] = vz;

            // Aerodynamic moments from the centre-of-pressure shift. Signs are chosen so the
            // plate weathervanes broadside to the flow: that restoring couple is the flutter
            // oscillator, while pieces spawned with high spin keep tumbling through it.
            const km = k * A.moment * 3;
            const tRoll = -km * this._invSpan[i] * v2 * vn;
            const tPitch = km * this._invChord[i] * v1 * vn;

            const spinMag = Math.sqrt(wxi * wxi + wyi * wyi + wzi * wzi);
            const speed = Math.sqrt(speed2);
            const damp = 1 / (1 + dt * (A.spinDampingLinear * k * speed + A.spinDampingQuadratic * spinMag));

            let wxn = (wxi + (tRoll * e1x + tPitch * e2x) * dt) * damp;
            let wyn = (wyi + (tRoll * e1y + tPitch * e2y) * dt) * damp;
            let wzn = (wzi + (tRoll * e1z + tPitch * e2z) * dt) * damp;

            const spin2 = wxn * wxn + wyn * wyn + wzn * wzn;

            if (spin2 > maxSpin * maxSpin) {
                const trim = maxSpin / Math.sqrt(spin2);

                wxn *= trim;
                wyn *= trim;
                wzn *= trim;
            }

            this._wx[i] = wxn;
            this._wy[i] = wyn;
            this._wz[i] = wzn;

            // dq/dt = 1/2 * omega (x) q, then renormalise.
            let nqx = qx + halfDt * (wxn * qw + wyn * qz - wzn * qy);
            let nqy = qy + halfDt * (wyn * qw + wzn * qx - wxn * qz);
            let nqz = qz + halfDt * (wzn * qw + wxn * qy - wyn * qx);
            let nqw = qw + halfDt * (-wxn * qx - wyn * qy - wzn * qz);

            const qlen = Math.sqrt(nqx * nqx + nqy * nqy + nqz * nqz + nqw * nqw);

            if (qlen > 1e-6) {
                const inv = 1 / qlen;

                nqx *= inv;
                nqy *= inv;
                nqz *= inv;
                nqw *= inv;
            }
            else {
                nqx = 0; nqy = 0; nqz = 0; nqw = 1;
            }

            this._qx[i] = nqx;
            this._qy[i] = nqy;
            this._qz[i] = nqz;
            this._qw[i] = nqw;

            let x = this._x[i] + vx * dt;
            let y = this._y[i] + vy * dt;

            if (walls && (x < 0 || x > W)) {
                x = x < 0 ? -x : 2 * W - x;
                vx = -vx * wallBounce;
                this._vx[i] = vx;
            }

            this._x[i] = x;
            this._y[i] = y;

            // One comparison per piece per step while something is watching, one per step otherwise.
            if (this._crestWatchers.length && y <= this._crestY) {
                this._crest();
            }

            const z = this._z[i] + vz * dt;

            // Bounced rather than clamped -- see physics.depthLimit for why depth needs a wall
            // at all and why stopping the piece dead at it would not do.
            if (z < -depthWall || z > depthWall) {
                const wall = z < 0 ? -depthWall : depthWall;

                this._z[i] = 2 * wall - z;
                this._vz[i] = -vz;
            }
            else {
                this._z[i] = z;
            }

            this._age[i] += dt;

            // NaN guard: one bad frame must not poison the whole system.
            if (!(x === x && y === y && nqw === nqw)) {
                this._kill(i);
                continue;
            }

            if (this._age[i] >= this._life[i] || x < -margin || x > W + margin || y > H + margin) {
                this._kill(i);
                continue;
            }

            // A piece still inside its launch jet is travelling with its own air, not resting
            // on anything: it must not land yet. This is what lets a fountain fire from below
            // the floor line without gluing its shot to the nozzle.
            // Only paper over the container can come to rest on it. A piece past the edge
            // carries on down and the bounds check below removes it, rather than banking up
            // against a wall the container does not actually have.
            if (pile && jet <= this._age[i] && x >= 0 && x <= W) {
                // Touching paper, or the floor. Asking the grid rather than a column height is
                // the whole point: a piece over a hollow finds empty cells and keeps falling.
                if (y >= floor || this._occupied(x, y)) {
                    // The floor is solid. A gust from above drives paper straight through it
                    // otherwise, and the never-rise rule then pins it there: measured after a
                    // hard sweep, 1435 of 1437 settled pieces were under the container, as
                    // deep as 159px, invisible to the grid and clipped out of the drawing
                    // while still counting as settled paper.
                    if (y > floor) {
                        y = floor;
                        this._y[i] = floor;
                    }

                    if (vy > 90 && this._bounce[i] < pile.bounces) {
                        this._bounce[i]++;
                        this._y[i] = y - vy * dt - 1;
                        this._vy[i] = -vy * pile.restitution;
                        this._vx[i] = vx * pile.friction;
                        this._wz[i] = wzn * 1.4;
                    }
                    else if (vx * vx + vy * vy > pile.settleSpeed * pile.settleSpeed) {
                        // Skid, do not freeze: the piece keeps the sideways travel it arrived
                        // with and slithers a little before stopping, which is how paper comes
                        // to rest on a heap. Undo exactly the fall this substep took and no
                        // more — moving it toward a surface instead is what used to walk a
                        // buried piece back up through the drift, leaving a hole behind it.
                        const damp = 1 / (1 + dt * pile.slide);

                        this._y[i] = y - Math.max(0, vy * dt);
                        this._vy[i] = 0;
                        this._vx[i] = vx * damp;
                        this._wz[i] = wzn * damp;
                    }
                    else {
                        this._settle(i);
                        continue;
                    }
                }
            }

            i++;
        }
    }

    /* -------------------------------------------------------------- *
     * Rendering — one fill per piece
     * -------------------------------------------------------------- */

    _render() {
        const ctx = this._ctx;
        const W = this._width;
        const H = this._height;

        /*
         * Clearing a canvas that is already blank is not free. It is a write over the whole
         * backing store that the rasteriser has to make, and on a parked stage the loop was
         * making one per pointer move: pointermove wakes the loop unconditionally, the frame
         * finds nothing to simulate, and then wipes the full surface to draw nothing on it.
         *
         * Measured at 1440x900 and dpr 2, with the drift settled and the air empty: 5.18M
         * device pixels per pointer move, which at a normal mouse speed is about 620M a
         * second. Cheap while the compositor has its fast path; with devtools open it does
         * not, and that was enough to wedge the browser around a page that looked idle.
         */
        if (this._painted) {
            ctx.clearRect(0, 0, W, H);
            this._painted = false;
        }

        this._fills = 0;

        if (!this._count) {
            return;
        }

        const r = this.options.render;
        const lx = this._lx;
        const ly = this._ly;
        const lz = this._lz;
        const fog = r.depthFog;
        const focal = this.options.physics.perspective;
        const depthNorm = 1 / (focal * 0.7);
        const fade = Math.max(0.01, this.options.life.fade);
        let lastAlpha = 1;

        ctx.globalAlpha = 1;

        for (let i = 0; i < this._count; i++) {
            const z = this._z[i];
            const scale = focal / (focal + z);

            if (scale <= 0.02) {
                continue;
            }

            const qx = this._qx[i];
            const qy = this._qy[i];
            const qz = this._qz[i];
            const qw = this._qw[i];
            const xx = qx * qx;
            const yy = qy * qy;
            const zz = qz * qz;
            const xy = qx * qy;
            const xz = qx * qz;
            const yz = qy * qz;
            const wx2 = qw * qx;
            const wy2 = qw * qy;
            const wz2 = qw * qz;

            // Only the projected (x, y) parts of e1/e2 are needed; the foreshortening that
            // makes a piece look edge-on falls out of the orthographic projection for free.
            const hw = this._hw[i] * scale;
            const hh = this._hh[i] * scale;
            const ax = (1 - 2 * (yy + zz)) * hw;
            const ay = 2 * (xy + wz2) * hw;
            const bx = 2 * (xy - wz2) * hh;
            const by = (1 - 2 * (xx + zz)) * hh;

            const cx = this._x[i];
            const cy = this._y[i];
            const reach = Math.abs(ax) + Math.abs(bx) + Math.abs(ay) + Math.abs(by);

            if (cx + reach < 0 || cx - reach > W || cy + reach < 0 || cy - reach > H) {
                continue;
            }

            let alpha = 1;
            const remaining = this._life[i] - this._age[i];

            if (remaining < fade) {
                alpha = remaining / fade;

                if (alpha <= 0) {
                    continue;
                }
            }

            const nx = 2 * (xz + wy2);
            const ny = 2 * (yz - wx2);
            const nz = 1 - 2 * (xx + yy);

            const dot = nx * lx + ny * ly + nz * lz;
            const facing = dot < 0 ? -dot : dot;
            const f2 = facing * facing;
            const f4 = f2 * f2;
            const f8 = f4 * f4;
            const paper = this._papers[this._paper[i]] || this._papers[0];
            const haze = 1 - fog * clamp(z * depthNorm + 0.5, 0, 1);
            const shade = (r.ambient + paper.albedo * r.diffuse * facing + r.specular * f8 * f8) * haze;

            if (alpha !== lastAlpha) {
                ctx.globalAlpha = alpha;
                lastAlpha = alpha;
            }

            ctx.fillStyle = this._shadeColor(paper, nz < 0 ? 0 : 1, shade);
            ctx.beginPath();
            ctx.moveTo(cx - ax - bx, cy - ay - by);
            ctx.lineTo(cx + ax - bx, cy + ay - by);
            ctx.lineTo(cx + ax + bx, cy + ay + by);
            ctx.lineTo(cx - ax + bx, cy - ay + by);
            ctx.fill();
            this._fills++;
        }

        ctx.globalAlpha = 1;
        this._painted = this._fills > 0;
    }

    /* -------------------------------------------------------------- *
     * Loop
     * -------------------------------------------------------------- */

    /**
     * One gust of the blower, at a point, at `share` times its per-frame strength. Both the
     * hover and the tap come through here so that they cannot drift apart: a tap is meant to
     * feel like the same blower, just once.
     */
    _gust(x, y, share, lifting) {
        const config = this._interactOptions();
        const pile = this._pileOptions();

        this.blow(x, y, {
            radius: config.radius,
            strength: config.idle * share,
            lift: config.lift,
            lifting,
            // A flat chance per lift, deliberately not scaled by how long it has been since
            // the last one. The gust takes whatever is in its circle and the circle refills in
            // between, so the same pieces are lifted per second at any liftRate -- measured,
            // 18.0 per frame against 18.9 at a quarter of the rate. Paying for the waiting
            // time as well therefore charged each of those lifts four times over: the same
            // sweep carried off 210 pieces instead of 57.
            carryAway: pile ? clamp(pile.sweepAway, 0, 1) : 0,
        });
    }

    /**
     * The steady gust under the pointer, emitted by the frame rather than by pointer events,
     * so it keeps blowing while the hand is still. Sweeping still adds its own push on top,
     * through the pointermove handler, exactly as before.
     *
     * Scaled by the length of the frame against 60 of them a second, so the same second of
     * hovering does the same thing whatever the display is running at.
     */
    _blowPointer(seconds) {
        const config = this._interactOptions();

        if (!config || !config.idle || !this._pointerOn || seconds <= 0) {
            return;
        }

        // Over the stage, not merely last seen somewhere. pointerleave is not guaranteed to
        // arrive -- a pointer captured elsewhere, a window losing focus mid-gesture, a leave
        // swallowed at a boundary -- and a missed one left this blowing into the last place
        // the pointer had been, from outside the container, for as long as the loop ran.
        // Bounds are the truth here and cost two comparisons.
        if (this._pointerX < 0 || this._pointerX > this._width
            || this._pointerY < 0 || this._pointerY > this._height) {
            return;
        }

        const interval = config.liftRate > 0 ? 1 / config.liftRate : 0;

        this._liftDue += seconds;

        const lifting = this._liftDue >= interval;

        if (lifting) {
            this._liftDue = 0;
        }

        this._gust(this._pointerX, this._pointerY, Math.min(seconds * 60, 2), lifting);
    }

    _wake() {
        if (this._raf || this.destroyed) {
            return;
        }

        this._idle = false;

        // One nominal frame back, not zero. Zero makes the next frame compute dt = 0, which
        // advances nothing at all -- and when the only thing that could put paper back in the
        // air is the gust that dt drives, nothing comes alive, so the loop stops again and
        // the next pointer move repeats it. Measured on a settled drift: waving the pointer
        // for 240 frames took 0 simulation steps and lifted 0 pieces, and only firing fresh
        // paper broke the cycle, because that alone makes the loop keep itself armed.
        //
        // Waking after a long sleep is already safe without this: the frame clamps dt, and
        // the accumulator clamps again to maxSubsteps.
        this._lastFrame = performance.now() - 1000 / 60;
        this._raf = requestAnimationFrame(this._frame);
    }

    _frame = (now) => {
        if (this.destroyed) {
            this._raf = 0;

            return;
        }

        // More than one callback can be queued at a time: _raf only remembers the last id
        // handed out, so a _wake() landing while an earlier callback is still waiting queues
        // a second one, and every entry re-arms on its way out, so the count never comes back
        // down. Pointer movement calls _wake() on every report, which is what climbs it.
        //
        // Measured while waving the pointer over a hundred pieces: 118 frames were displayed
        // per second and this ran 3732 times -- 32 full renders for every frame the screen
        // actually showed. It also made the frame counter meaningless, since it timed the
        // gaps between these, not between pictures.
        //
        // Every callback fired in the same display frame is handed the same timestamp, so the
        // duplicates are recognisable: do the work once and let the rest fall away without
        // re-arming, which drains the backlog in a single frame.
        if (now === this._frameStamp) {
            return;
        }

        this._frameStamp = now;

        // RUNNING, not idle, for the whole of the body below. Clearing _raf here instead let
        // anything the frame calls believe the loop had stopped -- and _scour ends by calling
        // _wake(), which then queues a second callback AND pushes _lastFrame forward by a
        // nominal frame. The next frame therefore integrated more time than had really
        // passed, so the whole scene ran fast the moment the pointer started blowing, while
        // a plain drop kept perfect time.
        //
        // A duplicate callback above returns without touching this, so the pending id that
        // the real frame armed is never thrown away either.
        this._raf = RUNNING;

        // The sentinel above says "a frame is in hand", which is exactly what _wake() checks
        // before scheduling one. If anything below throws, the re-arming at the end never
        // runs and that sentinel is left behind -- so _wake() believes a frame is already
        // coming, forever, and nothing can restart the loop again. One bad frame then kills
        // the whole thing silently: not just the animation, but every button that fires paper,
        // because they all go through _wake() too.
        //
        // Clearing it here costs nothing and means the next wake, from a pointer move or a
        // button, picks the loop back up. The error still reaches the console, which is where
        // it belongs.
        try {

            if (this._paused || !this._pageVisible || !this._onScreen) {
                this._lastFrame = now;
                this._raf = requestAnimationFrame(this._frame);

                return;
            }

            const elapsed = this._lastFrame ? (now - this._lastFrame) / 1000 : 0;
            // Physics is clamped so a long stall is not integrated in one go. Reporting is not.
            const dt = Math.min(elapsed, 0.1);

            this._lastFrame = now;

            // Measured from the REAL interval. Using the clamped one put a floor under the
            // counter at 1/0.1 = 10fps, so no stall could ever be reported as worse than that,
            // and the average smoothed away what was left. The slowest frame of the last second
            // is kept beside it, because one bad frame in sixty still averages out to fine.
            //
            // Both count how often this frame callback runs, which is not the same as how often
            // the screen updates: work handed to the compositor lands after this returns. A
            // counter reading 60 while the picture visibly stutters is therefore evidence in
            // itself -- it says the cost is not on this thread.
            if (elapsed > 0) {
                const ms = elapsed * 1000;

                this._fps = this._fps * 0.9 + 1 / elapsed * 0.1;

                if (now - this._worstAt > 1000) {
                    this._worstFrame = ms;
                    this._worstAt = now;
                }
                else if (ms > this._worstFrame) {
                    this._worstFrame = ms;
                }
            }

            this._pump(dt);
            this._blowPointer(dt);

            const P = this.options.physics;

            this._accumulator = Math.min(this._accumulator + dt, P.timeStep * P.maxSubsteps);

            while (this._accumulator >= P.timeStep) {
                this._step(P.timeStep);
                this._accumulator -= P.timeStep;
            }

            // Pieces lifted out of the pile leave holes in the baked canvas; repaint it at most
            // once a frame, however many gusts landed since the last one.
            if (this._pileDirty) {
                this._pileDirty = false;
                this._redrawPile(this._dirtyX0, this._dirtyY0, this._dirtyX1, this._dirtyY1);
            }

            this._render();

            if (this._count || this._streams.length) {
                this._raf = requestAnimationFrame(this._frame);
            }
            else {
                this._raf = 0;

                if (!this._idle) {
                    this._idle = true;

                    // Last, and outside anything that still needs this instance: a handler is
                    // entitled to destroy() from here, which is the whole point of the event.
                    if (this.options.onIdle) {
                        this.options.onIdle();
                    }
                }

                if (!this.destroyed && this._autoDestroy) {
                    this.destroy();
                }
            }
        }
        finally {
            if (this._raf === RUNNING) {
                this._raf = 0;
            }
        }
    };

    /** Feeds continuous emitters, keeping fractional leftovers so low rates stay smooth. */
    _pump(dt) {
        if (!dt) {
            return;
        }

        for (let s = this._streams.length - 1; s >= 0; s--) {
            const handle = this._streams[s];
            const slice = Math.min(dt, handle.remaining);

            handle.carry += handle.rate * slice;

            const emit = Math.floor(handle.carry);

            if (emit > 0) {
                handle.carry -= emit;
                handle.specs.forEach((spec) => this._spawn(spec, emit / handle.specs.length));
            }

            handle.remaining -= dt;

            if (handle.remaining <= 0) {
                this._streams.splice(s, 1);
                this._armPileFade();
            }
        }
    }

    pause() {
        this._paused = true;

        return this;
    }

    resume() {
        this._paused = false;
        this._lastFrame = 0;
        this._wake();

        return this;
    }

    /** Applies option changes at runtime (colours, gravity, wind, pile, ...). */
    configure(options = {}) {
        // One instance, one container, for its whole life. Nothing here can move a mounted
        // layer to another parent, resize the stage to a different box, rebind the pointer to
        // a different host and rebuild the drift to match -- so quietly accepting the key left
        // options.container saying one thing while the instance rendered somewhere else.
        // Make a second instance instead; they are cheap, and destroy() cleans up after the
        // first.
        if ('container' in options && options.container !== this._mountedOn) {
            throw new Error('Confetti: container is fixed for the life of an instance; create a second one instead');
        }

        this.options = merge(this.options, options);

        if (options.papers) {
            this._buildPapers();
        }

        if (options.render) {
            this._buildLighting();

            // Settled paper is baked into its own canvas, so it does not repaint itself every
            // frame the way the airborne pieces do. Without this, the light changes for paper
            // in the air and the drift underneath keeps the old one.
            this._markDirty(0, 0, this._width, this._height);
            this._wake();
        }

        if (options.pile !== undefined) {
            this._pileCache = null;
            this._ensurePileCanvas();
            this._initPile();
        }

        if (options.interactive !== undefined) {
            this._interactCache = null;
            this._unbindPointer();
            this._bindPointer();
        }

        return this;
    }

    stats() {
        return {
            alive: this._count,
            capacity: this._capacity,
            settled: this._settledCount,
            fills: this._fills,
            fps: Math.round(this._fps),
            worstFrameMs: Math.round(this._worstFrame),
            streams: this._streams.length,
            width: this._width,
            height: this._height,
        };
    }

    destroy() {
        if (this.destroyed) {
            return;
        }

        this.destroyed = true;
        this._streams.length = 0;
        this._count = 0;
        this._cancelPileFade();
        this._crestWatchers.length = 0;

        if (this._raf) {
            cancelAnimationFrame(this._raf);
            this._raf = 0;
        }

        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
        }

        if (this._intersectionObserver) {
            this._intersectionObserver.disconnect();
        }

        if (this._onScroll) {
            window.removeEventListener('scroll', this._onScroll, { capture: true });
            this._onScroll = null;
        }

        if (this._onResize) {
            window.removeEventListener('resize', this._onResize);
            window.removeEventListener('orientationchange', this._onResize);
        }

        if (this._onVisibility) {
            document.removeEventListener('visibilitychange', this._onVisibility);
        }

        this._unbindPointer();

        if (this._layer && this._layer.parentNode) {
            this._layer.parentNode.removeChild(this._layer);
        }
    }

}

Confetti.presets = PRESETS;
Confetti.palettes = PALETTES;
Confetti.defaults = DEFAULTS;
Confetti.pileDefaults = PILE_DEFAULTS;
Confetti.emitDefaults = EMIT_DEFAULTS;
Confetti.interactionDefaults = INTERACT_DEFAULTS;
